import { useState, useRef, useEffect, useCallback } from 'react';
import { Tooltip } from 'react-tooltip';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { useSearchParams } from 'react-router-dom';

import * as validate from '../../utils/phoneValidation.js';
import useApi from '../../utils/useApi';
import 'react-tooltip/dist/react-tooltip.css';
import '../../css/common-styles.css';
import '../../css/common-components.css';
import loadIcon from '../../images/loader-transparent.svg';
import Toaster from '../Toaster/Toaster.jsx';
import Error from '../Error/Error.jsx';
import errorContent from '../../utils/ErrorMessages.js';
import InternationalPhoneInput from '../InternationalPhoneInput/InternationalPhoneInput.jsx';
import AddCC from '../CC/AddCC.jsx';
import DocumentSettingsSection from '../DocumentSettings/DocumentSettings.jsx';
import FileUploadManager from '../FileUploadManager/FileUploadManager.jsx';
import { ALLOWED_COUNTRIES, DEFAULT_COUNTRY } from '../../utils/flagsConfig.js';
import MissingLocaleMessageDialogBox from '../MissingLocaleMessageDialogBox/MissingLocaleMessageDialogBox.jsx';

import styles from './TemplateSend.module.css';


const TemplateSend = () => {

  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showErrorPage, setShowErrorPage] = useState(false);
  const [searchParams] = useSearchParams();
  const isPopup = searchParams.has('popup');
  const [errorPageConfig, setErrorPageConfig] = useState({});
  
  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const recordId = urlParams.get('recordId');
  const objectType = urlParams.get('objectType') || '';
  const templateId = urlParams.get('selectedTemplated');
  const [selectedTemplate, setSelectedTemplate] = useState(templateId);
  const [currentStep, setCurrentStep] = useState(0);
  const [allowNewFiles, setAllowNewFiles] = useState(false);
  const [allowModifyFiles, setAllowModifyFiles] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const hasIncompleteUploads = [...uploadedFiles, ...uploadingFiles].some(f => f.progress < 100);
  const [documents, setDocuments] = useState({ title: '', message: '' });
  const [popup, setPopup] = useState('');
  const [templateFiles, setTemplateFiles] = useState([]);
  
  const MAX_RECIPIENTS = 50;

  const api = useApi();

  // Run the previous "handleSendClick" logic when the component initializes (or when URL templateId changes)
  useEffect(() => {
    const loadTemplateOnInit = async () => {
      setCurrentStep(1);
      setIsLoading(true);
      if (!selectedTemplate) {
        // If templateId wasn't supplied in URL, show an error page
        setErrorPageConfig({
          title: errorContent.default.title,
          message: errorContent.default.message,
        });
        setShowErrorPage(true);
        return;
      }

      try {
        // Fetch template properties
        const response = await api.get('/boldsign/template-properties', {
          params: { templateId: selectedTemplate }
        });

        // NOTE: Fix the status check: use "!== 200" instead of "!response.status === 200"
        if (response.status !== 200) {
          const errorData = response?.data;
          const parsed = typeof errorData === 'string' ? (() => { try { return JSON.parse(errorData); } catch { return {}; } })() : (errorData || {});
          if (parsed.error === 'TemplateNotSupported') {
            setErrorPageConfig({
              title: 'Template Not Supported.',
              message: 'The selected template cannot be used with the HubSpot integration because it includes unsupported settings. Please choose a different template to continue. You can close this window now.'
            });
          } else {
            setErrorPageConfig({
              title: errorContent.loadTemplates.title,
              message: errorContent.loadTemplates.message
            });
          }
          setShowErrorPage(true);
          return;
        }

        const templateData = await response.data;
        // Set template details (read-only)
        setTemplateDetails({
          title: templateData.messageTitle || '',
          description: templateData.documentDescription || ''
        });
        setAllowNewFiles(Boolean(templateData.allowNewFiles));
        setAllowModifyFiles(Boolean(templateData.allowModifyFiles));

        // Extract existing template files and sort by Order
        if (templateData.files && Array.isArray(templateData.files)) {
          const sortedFiles = templateData.files
            .map(file => ({
              id: file.Id || `template-file-${Math.random().toString(36).substring(9)}`,
              name: file.documentName || '',
              pageCount: file.pageCount || 1,
              order: file.order || 0,
              isTemplateFile: true
            }))
            .sort((a, b) => a.order - b.order);
          setTemplateFiles(sortedFiles);
        }

        // Initialize recipients based on template roles
        if (templateData.roles && templateData.roles.length > 0) {
          const initialRecipients = templateData.roles.map((role, index) => {
            let languageCode = 'EN';
            let templateDeliveryMode = 'Email';
            if (typeof role.locale === 'number') {
              languageCode = getLanguageCodeFromLocale(role.locale);
            } else if (typeof role.locale === 'string' && role.locale.trim() !== '') {
              languageCode = role.locale;
            }

            if (typeof role.deliveryMode === 'number') {
              templateDeliveryMode = getDeliveryOptionFromNumber(role.deliveryMode);
            } else if (typeof role.deliveryMode === 'string' && role.deliveryMode.trim() !== '') {
              templateDeliveryMode = role.deliveryMode;
            }

            // Extract ISO code from phone's countryCode (e.g., "us" from "+1")
            let isoCode = DEFAULT_COUNTRY;
            let numberWithCountryCode = role.phoneNumber?.countryCode + role.phoneNumber?.number;
            const fullPhoneNumber = numberWithCountryCode || '';
            if (fullPhoneNumber) {
              const parsedPhone = parsePhoneNumberFromString(fullPhoneNumber);
              isoCode = parsedPhone?.country?.toLowerCase() || DEFAULT_COUNTRY;
            }

            let authenticationType = getAuthenticationType(role.imposeAuthentication);
            return {
              id: role.index || index + 1,
              roleName: role.name || '',
              name: role.defaultSignerName || '',
              email: role.defaultSignerEmail || '',
              iso: isoCode,
              fullPhone: fullPhoneNumber,
              phone: role.phoneNumber?.number ?? '',
              countryCode: role.phoneNumber?.countryCode ?? '',
              deliveryOption: templateDeliveryMode,
              language: languageCode,
              role: role.signerType === 1 ? 'signer' : 'reviewer',
              order: role.signerOrder || index + 1,
              roleIndex: role.index,
              isFromTemplate: true,
              allowRoleEdit: role.allowRoleEdit,
              allowRoleDelete: role.allowRoleDelete,
              templateBrandId: templateData.brandId,
              tags: Array.isArray(templateData.labels)
                ? templateData.labels.flatMap(tag =>
                    tag.split(',').map(t => t.trim()).filter(Boolean)
                  )
                : [],
              enablePrintAndSign: templateData.enablePrintAndSign,
              enableReassign: templateData.enableReassign,
              allowFieldConfigurationSetting: role.allowFieldConfiguration,
              privateMessage: '',
              accessCode: '',
              showAuthenticationSettings: authenticationType !== 'none',
              authType: authenticationType === 'none' ? 'accessCode' : authenticationType,
              enableEmailOTP: role.enableEmailOTP,
              enableAccessCode: role.enableAccessCode,
            };
          });

          setTemplateRolesLength(templateData.roles.length);
          setTemplateBrandId(templateData.brandId);

          setDocumentSettings(prev => ({
            ...prev,
            tags: templateData.labels || [],
            enablePrintAndSign: templateData.enablePrintAndSign,
            enableReassign: templateData.enableReassign
          }));
          setRecipients(initialRecipients);
          setOriginalTemplateRoleIndices(initialRecipients.map(r => r.roleIndex).filter(Boolean));
          setSigningOrder(templateData.enableSigningOrder || false);
          const colorMapping = {};
          initialRecipients.forEach((r, i) => {
            colorMapping[r.id] = fixedColorSequence[i % fixedColorSequence.length];
          });
          setRecipientColors(colorMapping);
          setErrors(prev => ({
            ...prev,
            recipients: initialRecipients.map(() => ({ name: '', email: '', roleName: '', phone: '' }))
          }));
        }

        // Set document details from template
        setDocumentDetails({
          title: templateData.documentTitle || '',
          message: templateData.documentMessage || '',
          isAllowEditMessage: Boolean(templateData.allowMessageEditing),
          allowNewRoles: Boolean(templateData.allowNewRoles)
        });

        const initialRecipientLocales = templateData.roles
          .map((r) => {
            const locale = r.locale ?? r.language;
            if (typeof locale === 'number'
                || (typeof locale === 'string' && /^\d+$/.test(locale.trim()))) {
              return getLanguageCodeFromLocale(Number(locale));
            }
            return typeof locale === 'string' ? locale.trim().toUpperCase() : '';
          })
          .filter(Boolean);
        const uniqueSeedLocales = [];
        initialRecipientLocales.forEach((loc) => {
          if (!uniqueSeedLocales.includes(loc)) uniqueSeedLocales.push(loc);
        });
        const rawDocInfos = (Array.isArray(templateData.documentInfo) && templateData.documentInfo) || [];
        const seededInfo = {};
        rawDocInfos.forEach((entry, index) => {
          if (!entry) return;
          let key = entry.locale ?? entry.language;
          if (typeof key === 'number'
              || (typeof key === 'string' && /^\d+$/.test(key.trim()))) {
            const mappedKey = getLanguageCodeFromLocale(Number(key));
            key = uniqueSeedLocales.includes(mappedKey) ? mappedKey : (uniqueSeedLocales[index] || mappedKey);
          }
          if (!key || typeof key !== 'string' || !key.trim()) return;
          key = key.trim().toUpperCase();
          seededInfo[key] = {
            title: entry.title || templateData.documentTitle || '',
            message: entry.message ?? entry.description ?? templateData.documentMessage ?? ''
          };
        });
        setDocumentInfo(seededInfo);

        let activeSeedLocale = activeLocale;
        if (uniqueSeedLocales.length === 1) {
          activeSeedLocale = uniqueSeedLocales[0];
        } else if (uniqueSeedLocales.length > 1 && !uniqueSeedLocales.includes(activeSeedLocale)) {
          activeSeedLocale = uniqueSeedLocales[0];
        }
        setActiveLocale(activeSeedLocale);
        const fallbackTitle = templateData.documentTitle || '';
        const fallbackMessage = templateData.documentMessage || '';

        uniqueSeedLocales.forEach((locale) => {
          if (!seededInfo[locale]) {
            seededInfo[locale] = {
              title: fallbackTitle,
              message: fallbackMessage
            };
          }
        });
        setDocumentInfo(seededInfo);

        const seededEntry = seededInfo[activeSeedLocale] || { title: fallbackTitle, message: fallbackMessage };
        const seededTitle = seededEntry.title || fallbackTitle || '';
        const seededMessage = seededEntry.message || fallbackMessage || '';
        setDocuments({ title: seededTitle, message: seededMessage });

        lastWrittenDocDetailsRef.current = { title: seededTitle, message: seededMessage };
        skipDocMirrorRef.current = false;

        setTemplateBrandId(templateData.brandId || null);
        
        // Map numeric documentDownloadOption to string values:
        // 0 = 'combined', 1 = 'individually'
        let downloadOption = 'combined'; // default
        if (typeof templateData.documentDownloadOption === 'number') {
          downloadOption = templateData.documentDownloadOption === 1 ? 'individually' : 'combined';
        } else if (typeof templateData.documentDownloadOption === 'string') {
          downloadOption = templateData.documentDownloadOption.toLowerCase() === 'combined' ? 'combined' : 'individually';
        }
        
        setDocumentSettings(prev => ({
          ...prev,
          // Pre-select the template's brand once the brands list is loaded.
          // If brands are not yet loaded, DocumentSettingsSection will sync
          // from its own default-isDefault fallback; the template brand is
          // still re-applied below when brands arrive.
          selectedBrand: prev.selectedBrand,
          enablePrintAndSign: templateData.enablePrintAndSign ?? prev.enablePrintAndSign,
          enableReassign: templateData.enableReassign ?? prev.enableReassign,
          documentDownloadOption: downloadOption,
          tags: Array.isArray(templateData.labels) ? [...templateData.labels] : prev.tags
        }));

        // Set CC recipients (convert to tags)
        const ccData = templateData.ccDetails;
        if (ccData) {
          let ccEmails = [];
          if (Array.isArray(ccData)) {
            ccEmails = ccData;
          } else if (typeof ccData === 'string') {
            ccEmails = ccData.split(',').map(e => e.trim()).filter(Boolean);
          }

          if (ccEmails.length > 0) {
            setCcEmailTags(ccEmails);
            setCcRecipients(ccEmails.join(', '));
            setTemplateCcEmails(ccEmails);
          }
        } else {
          setCcRecipients('');
          setCcEmailTags([]);
          setTemplateCcEmails([]);
        }

        // We are now in "Prepare document" page
        setCurrentStep(1);
      } catch (error) {
        console.error('Error loading template properties:', error);
        setErrorPageConfig({
          title: errorContent.loadTemplates.title,
          message: errorContent.loadTemplates.message
        });
        setShowErrorPage(true);
      } finally {
        setIsLoading(false);
      }
    };

    // Run on init and if the URL's templateId changes
    if (selectedTemplate) {
      loadTemplateOnInit();
    } else {
      // If selectedTemplate isn't set yet but URL has it, keep them in sync
      if (selectedTemplate) setSelectedTemplate(selectedTemplate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const languageNames = {
    'EN': 'English',
    'ES': 'Spanish',
    'DE': 'German',
    'FR': 'French',
    'RO': 'Romanian',
    'NO': 'Norwegian',
    'BG': 'Bulgarian',
    'IT': 'Italian',
    'DA': 'Danish',
    'PL': 'Polish',
    'PT': 'Portuguese',
    'CS': 'Czech',
    'NL': 'Dutch',
    'RU': 'Russian',
    'SV': 'Swedish',
    'ZH_CN':'SimplifiedChinese',
    'ZH_TW':'TraditionalChinese',
    'JA':'Japanese',
    'TH':'Thai',
    'KO':'Korean'
  };

  // Mapping from numeric locale codes to string codes
  // Based on the pattern observed: 0=EN, 1=NO, 2=FR, 3=DE, etc.
  const getLanguageCodeFromLocale = (localeNumber) => {
    

  const localeMapping = {
    0: 'EN',      //English
    1: 'NO',      //Norwegian
    2: 'FR',      //French
    3: 'DE',      //German
    4: 'ES',      //Spanish
    5: 'BG',      //Bulgarian
    6: 'CS',      //Czech
    7: 'DA',      //Danish
    8: 'IT',      //Italian
    9: 'NL',      //Dutch
    10: 'PL',     //Polish
    11: 'PT',     //Portuguese
    12: 'RO',     //Romanian
    13: 'RU',     //Russian
    14: 'SV',     //Swedish
    15: 'Default',//Default
    16: 'JA',     //Japanese
    17: 'TH',     //Thai
    18: 'ZH_CN',  //SimplifiedChinese   
    19: 'ZH_TW',  //TraditionalChinese
    20: 'KO'      //Korean
  };


    
    return localeMapping[localeNumber] || 'EN'; // Default to English if not found
  };

  // Mapping from numeric delivery mode codes to string codes
  // Based on the pattern observed: 0=Email, 1=SMS, 2=EmailAndSMS, etc.
  const getDeliveryOptionFromNumber = (deliveryModeNumber) => {
    const DeliveryModeMapping = {
      0: 'Email',
      1: 'SMS',
      2: 'EmailAndSMS',
    };

    return DeliveryModeMapping[deliveryModeNumber] || 'Email'; // Default to Email if not found
  };

  const getAuthenticationType = (imposeAuthenticationCode) => {
    const AuthenticationType = {
      1: 'none',
      2: 'email',
      3: 'accessCode',
      4: 'SMS'
    };

    return AuthenticationType[imposeAuthenticationCode] || 'none'; // Default to none if not found
  };

  const recipientRoles = [
    { id: 'signer', name: 'Signer', icon: <div className='signer-icon'></div> },
    { id: 'reviewer', name: 'Reviewer', icon: <div className='reviewer-icon'></div> }
  ];

  const fixedColorSequence = [
    'rgb(253, 247, 219)',
    'rgb(214, 255, 221)',
    'rgb(217, 244, 254)',
    'rgb(244, 221, 255)',
    'rgb(221, 228, 255)',
    'rgb(219, 253, 247)'
  ];

  // State management
  const [templateDetails, setTemplateDetails] = useState({ title: '', description: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [templateRolesLength, setTemplateRolesLength] = useState(0);
  const [originalTemplateRoleIndices, setOriginalTemplateRoleIndices] = useState([]);
  const [recipientColors, setRecipientColors] = useState(() => ({}));

  const [signingOrder, setSigningOrder] = useState(false);
  const [documentDetails, setDocumentDetails] = useState({ title: '', message: '', isAllowEditMessage: false, allowNewRoles: false });

  const [documentInfo, setDocumentInfo] = useState({});
  const [activeLocale, setActiveLocale] = useState('EN');

  const lastWrittenDocDetailsRef = useRef({ title: '', message: '' });

  const skipDocMirrorRef = useRef(false);

  const syncingDocumentsRef = useRef(false);

  // Brands list for DocumentSettingsSection
  const [brandsList, setBrandsList] = useState([]);
  // Brand id predefined on the template (used to pre-select once brands load)
  const [templateBrandId, setTemplateBrandId] = useState(null);

  // Document settings - managed by DocumentSettingsSection component
  // Mirrors DocumentSend.jsx state shape so the same component can be reused
  const [documentSettings, setDocumentSettings] = useState({
    selectedBrand: null,
    tags: [],
    enablePrintAndSign: false,
    enableReassign: true,
    documentDownloadOption: 'combined',
    expiryValue: '60',
    enableExpiryAlert: false,
    enableAutoReminder: false,
    reminderDays: '2',
    reminderCount: '3'
  });

  const [ccRecipients, setCcRecipients] = useState('');
  const [ccEmailTags, setCcEmailTags] = useState([]);
  const [ccInputValue, setCcInputValue] = useState('');
  const [templateCcEmails, setTemplateCcEmails] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState({ type: null, id: null });
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [tempOrderValue, setTempOrderValue] = useState('');
  const [errors, setErrors] = useState({
    documentTitle: '',
    recipients: [],
    phone: '',
    cc: '',
    signerError: '',
    progressError: '',
    accessCode: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [iframeSrc, setIframeSrc] = useState('');
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [focusedInputId, setFocusedInputId] = useState(null);
  const [filteredContactSuggestions, setFilteredContactSuggestions] = useState([]);
  const [toasterMessage, setToasterMessage] = useState({ show: false, message: '', type: 'error' });

  const dropdownRefs = useRef({});
  const orderInputRef = useRef(null);
  const iframeRef = useRef(null);
  const recipientRowRefs = useRef({});
  const dragGhostRef = useRef(null);

  const [hoveredRecipientId, setHoveredRecipientId] = useState(null);
  const [showLocaleMessageDialog, setShowLocaleMessageDialog] = useState(false);
  const [missingMessageLocales, setMissingMessageLocales] = useState([]);

  // Add Me state variables
  const [addMeVisible, setAddMeVisible] = useState(true);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [cachedCurrentUser, setCachedCurrentUser] = useState(null);
  const [fetchedCurrentUser, setFetchedCurrentUser] = useState(false);
  const [addMeLoading, setAddMeLoading] = useState(false);

  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const reorderRecipientsByDrag = useCallback((fromId, toId) => {
    if (fromId === null || toId === null || fromId === toId) return;
  
    const sorted = [...recipients].sort((a, b) => a.order - b.order);
    const fromIndex = sorted.findIndex(r => r.id === fromId);
    const toIndex = sorted.findIndex(r => r.id === toId);
  
    if (fromIndex === -1 || toIndex === -1) return;
  
    const [moved] = sorted.splice(fromIndex, 1);
    sorted.splice(toIndex, 0, moved);
  
    // Reassign order values and update the entire recipient list
    const updated = sorted.map((r, idx) => ({
      ...r,
      order: idx + 1
    }));
  
    setRecipients(updated);
  }, [recipients]);
const onDragStartHandle = (e, id) => {
  if (!signingOrder) return;
  setDraggingId(id);
  try {
    e.dataTransfer.effectAllowed = 'move';
    const rowEl = recipientRowRefs.current[id];
    if (rowEl && e.dataTransfer.setDragImage) {
      // Create a lightweight ghost to avoid browser selection blue line
      const ghost = rowEl.cloneNode(true);
      // Apply shared styles via CSS class
      ghost.classList.add('drag-ghost');
      // Preserve dynamic sizing via inline width to match the dragged element
      ghost.style.width = rowEl.offsetWidth + 'px';
      document.body.appendChild(ghost);
      dragGhostRef.current = ghost;
      const rect = rowEl.getBoundingClientRect();
      // Slight offset to avoid jump
      e.dataTransfer.setDragImage(ghost, rect.width/80, rect.height/80);
    }
  } catch {
    // ignore drag image compatibility issues
  }
};
const onDragOverRow = (e, targetId) => {
  if (!signingOrder) return;
  e.preventDefault();
  try { e.dataTransfer.dropEffect = 'move'; } catch {
    // ignore unsupported dropEffect
  }
  // Live-reorder while hovering
  setDragOverId(prev => {
    if (draggingId !== null && targetId && targetId !== draggingId && targetId !== prev) {
      reorderRecipientsByDrag(draggingId, targetId);
    }
    return targetId ?? null;
  });
};
const onDropRow = (e, targetId) => {
  if (!signingOrder) return;
  e.preventDefault();
  if (draggingId !== null) {
    reorderRecipientsByDrag(draggingId, targetId);
  }
  setDraggingId(null);
  setDragOverId(null);
  // Clean up drag ghost
  if (dragGhostRef.current && dragGhostRef.current.parentNode) {
  try { dragGhostRef.current.parentNode.removeChild(dragGhostRef.current); } catch {
    // ignore DOM removal errors
  }
    dragGhostRef.current = null;
  }
};

const onDragEndHandle = () => {
  // Ensure cleanup when drag is cancelled or ends outside a valid drop zone
  setDraggingId(null);
  setDragOverId(null);
  if (dragGhostRef.current && dragGhostRef.current.parentNode) {
  try { dragGhostRef.current.parentNode.removeChild(dragGhostRef.current); } catch {
    // ignore DOM removal errors
  }
    dragGhostRef.current = null;
  }
};

  // Handle iframe messages
  useEffect(() => {
    const handleMessage = (event) => {
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data.action === "DONE") {
          window.parent.postMessage(JSON.stringify({ action: "DONE" }), "*");
        }
      } catch (e) {
        console.error("Invalid message from iframe:", e);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Toaster functions
  const showToaster = (message, type = 'error') => {
    setToasterMessage({ show: true, message, type });
  };

  const hideToaster = () => {
    setToasterMessage({ show: false, message: '', type: 'error' });
  };

  useEffect(() => {
    let mounted = true;
    const fetchCurrent = async () => {
      if (fetchedCurrentUser) return;
      setFetchedCurrentUser(true);
      try {
        const resp = await api.get('/boldsign/get-current-user');
        if (resp && resp.status === 200) {
          const u = await resp.data;
          if (u && u.email) {
            setCachedCurrentUser(u); // Cache the user data
            const email = (u.email || '').trim().toLowerCase();
            if (mounted) setCurrentUserEmail(email);
            // If recipients already contain this email, hide Add Me
            const exists = recipients.some(r => (r.email || '').trim().toLowerCase() === email);
            if (mounted && exists) setAddMeVisible(false);
          }
        }
      } catch (e) {
        // Ignore errors for visibility; Add Me remains visible by default
        // console.debug('Could not fetch current BoldSign user for visibility', e);
      }
    };

    fetchCurrent();
    return () => { mounted = false; };
  }, []);

  // Recompute Add Me visibility when recipients or currentUserEmail change
  useEffect(() => {
    if (!currentUserEmail) return;
    const exists = recipients.some(r => (r.email || '').trim().toLowerCase() === currentUserEmail);
    setAddMeVisible(!exists);
  }, [recipients, currentUserEmail]);

  const getUniqueRecipientLocales = useCallback(() => {
    const seen = [];
    recipients.forEach((r) => {
      const locale = r.language || r.locale;
      if (locale && !seen.includes(locale)) seen.push(locale);
    });
    return seen;
  }, [recipients]);

  // Returns the list of unique locales whose message is currently empty.
  // Only relevant when the template allows message editing; otherwise messages
  // are not editable from the send page so the dialog should not appear.
  const getLocalesWithMissingMessage = useCallback(() => {
    if (!documentDetails.isAllowEditMessage) return [];
    const uniqueLocales = getUniqueRecipientLocales();
    if (uniqueLocales.length < 2) return [];
    return uniqueLocales.filter((loc) => !((documentInfo[loc]?.message || '').trim()));
  }, [getUniqueRecipientLocales, documentInfo, documentDetails.isAllowEditMessage]);

  useEffect(() => {
  const uniqueLocales = getUniqueRecipientLocales();
  if (uniqueLocales.length === 0) return;

  setDocumentInfo(prev => {
    const next = {};
    const seedTitle = (prev[uniqueLocales[0]] && prev[uniqueLocales[0]].title) || documents.title || '';

    uniqueLocales.forEach(loc => {
      next[loc] = prev[loc] || {
        title: seedTitle,
        message: ''
      };
    });

    return next;
  });

  setActiveLocale(prev =>
    uniqueLocales.includes(prev)
      ? prev
      : uniqueLocales[0]
  );
}, [getUniqueRecipientLocales]);

  useEffect(() => {
    const uniqueLocales = getUniqueRecipientLocales();
    if (uniqueLocales.length === 0) return;
    const active = documentInfo[activeLocale] || { title: '', message: '' };
    if (skipDocMirrorRef.current) {
      skipDocMirrorRef.current = false;
      lastWrittenDocDetailsRef.current = { title: active.title || '', message: active.message || '' };
      return;
    }
    const nextTitle = active.title || '';
    const nextMessage = active.message || '';
    if (lastWrittenDocDetailsRef.current.title !== nextTitle
        || lastWrittenDocDetailsRef.current.message !== nextMessage) {
      lastWrittenDocDetailsRef.current = { title: nextTitle, message: nextMessage };
      syncingDocumentsRef.current = true;
      setDocuments({ title: nextTitle, message: nextMessage });

      setDocumentDetails(prev => ({
        ...prev,
        title: nextTitle,
        message: nextMessage
      }));
    }
  }, [documentInfo, activeLocale, getUniqueRecipientLocales]);

  useEffect(() => {
    const uniqueLocales = getUniqueRecipientLocales();
    if (uniqueLocales.length === 0) return;
    if (syncingDocumentsRef.current) {
      syncingDocumentsRef.current = false;
      return;
    }
    const last = lastWrittenDocDetailsRef.current;
    if (documents.title === last.title && documents.message === last.message) {
      return;
    }

    lastWrittenDocDetailsRef.current = { title: documents.title, message: documents.message };
    skipDocMirrorRef.current = true;
    if (uniqueLocales.length === 1) {
      const only = uniqueLocales[0];
      setDocumentInfo((prev) => {
        const cur = prev[only] || { title: '', message: '' };
        if (cur.title === documents.title && cur.message === documents.message) return prev;
        return { ...prev, [only]: { title: documents.title, message: documents.message } };
      });
    } else {
      setDocumentInfo((prev) => {
        const cur = prev[activeLocale] || { title: '', message: '' };
        if (cur.title === documents.title && cur.message === documents.message) return prev;
        return { ...prev, [activeLocale]: { title: documents.title, message: documents.message } };
      });
    }
    setDocumentDetails(prev => ({
      ...prev,
      title: documents.title || '',
      message: documents.message || ''
    }));
  }, [documents.title, documents.message]);

  // Load brands list once on mount for the Document Settings section
  useEffect(() => {
    const loadBrands = async () => {
      try {
        const response = await api.get('/boldsign/get-brand-details');
        if (response.status === 200) {
          const data = await response.data;
          // Normalize shape so keys/labels are stable regardless of SDK casing
          const normalized = Array.isArray(data)
            ? data.map(b => ({
                id: b.brandId,
                brandName: b.brandName,
                isDefault: b.isDefault
              }))
            : [];
          setBrandsList(normalized);
        }
      } catch (error) {
        console.error('Failed to load brands', error);
      }
    };
    loadBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle document settings changes from DocumentSettingsSection component.
  // Mirrors the same pattern used in DocumentSend.jsx and TemplateCreate.jsx.
  const handleSettingsChange = useCallback((changedSettings) => {
    setDocumentSettings(prev => ({
      ...prev,
      ...changedSettings
    }));
  }, []);

  // Pre-select the template's brand on the Document Settings panel once the
  // brands list has finished loading. Falls back to the account default brand
  // when the template brand is not found among the available brands.
  useEffect(() => {
  if (!brandsList.length) return;

  const selectedBrand =
    brandsList.find(b => b.id === templateBrandId) ||
    brandsList.find(b => b.isDefault) ||
    brandsList[0];

  setDocumentSettings(prev => ({
    ...prev,
    selectedBrand
  }));
}, [brandsList, templateBrandId]);

  const handleAddMeClick = () => {
    // Use cached user data from initial fetch - no additional API call needed
    const user = cachedCurrentUser;
    
    if (!user || !user.email) {
      showToaster('BoldSign profile does not contain an email address.', 'error');
      return;
    }

    try {
      const userEmail = (user.email || '').trim().toLowerCase();
      const userName = (user.fullName || '').trim();

      // Validate email format before adding
      if (!isValidEmail(userEmail)) {
        showToaster('BoldSign returned an invalid email address.', 'error');
        return;
      }

      // If any recipient already has this email (case-insensitive), hide Add Me and do nothing
      const exists = recipients.some(r => (r.email || '').trim().toLowerCase() === userEmail);
      if (exists && !signingOrder) {
        setAddMeVisible(false);
        return;
      }

      // Find first empty recipient row (both name and email empty)
      const emptyIndex = recipients.findIndex(r => !(r.email && r.email.trim()) && !(r.name && r.name.trim()) && r.deliveryOption != 'SMS');
      if (emptyIndex !== -1) {
        const updated = recipients.map((r, idx) => idx === emptyIndex ? { ...r, name: userName, email: userEmail } : r);
        setRecipients(updated);
        setErrors(prev => {
          const newRecipientErrors = [...prev.recipients];
          if (newRecipientErrors[emptyIndex]) {
            newRecipientErrors[emptyIndex] = {
              ...newRecipientErrors[emptyIndex],
              name: '',
              email: ''
            };
          }
          return { ...prev, recipients: newRecipientErrors };
        });
      } else {
        // Append a new recipient without mutating other rows
        addRecipient();
        // After adding, set the last recipient to the user values
        setTimeout(() => {
          setRecipients(prev => {
            if (!prev || prev.length === 0) return prev;
            const lastIdx = prev.length - 1;
            const lastRecipientId = prev[lastIdx]?.id;
            const updated = prev.map((r, idx) => idx === lastIdx ? { ...r, name: userName, email: userEmail } : r);
            setErrors(prevErrors => {
              const newRecipientErrors = [...prevErrors.recipients];
              if (newRecipientErrors[lastIdx]) {
                newRecipientErrors[lastIdx] = {
                  ...newRecipientErrors[lastIdx],
                  name: '',
                  email: ''
                };
              }
              return { ...prevErrors, recipients: newRecipientErrors };
            });
            return updated;
          });
        }, 0);
      }

      // Hide Add Me after successful addition if signing order is disabled
      if (!signingOrder) {
        setAddMeVisible(false);
      }else {
        setAddMeVisible(true);
      }
      // Cache current user email for visibility rules
      setCurrentUserEmail(userEmail);
    }
    catch (err) {
      console.error('Add Me failed', err);
      showToaster('Failed to add current BoldSign user.', 'error');
    }
  };

  // Load contact data for suggestion
  const loadContactData = useCallback(async () => {
    try {
      let allContacts = [];
      // For contact object type, we need to get both primary and associated contacts
      if (objectType.toLowerCase() === 'contact') {
        try {
          // First, get the primary contact
          const primaryContactResponse = await api.get(
            `/hubspot/get-contacts`,
          {
            params:
            {
              contactId:recordId
            }
          });
          
          if (!primaryContactResponse.status === 200) {
            const errorText = await primaryContactResponse.data;
            let errorMessage = 'Failed to load primary contact information.';
            
            try {
              let errorData;
              if (typeof errorText === 'string'){
                errorData = JSON.parse(errorText);
              }
              else{
                errorData = errorText;
              }
              
              if (errorData.message) {
                errorMessage = errorData.message;
              }
            } catch (parseError) {
              if (errorText) {
                errorMessage = errorText.title;
              }
            }
            
            showToaster(errorMessage, 'error');
            return;
          }
          
          const primaryContact = await primaryContactResponse.data;
          if (primaryContact && primaryContact.name && primaryContact.email) {
            primaryContact.phoneNumberWithCountryCode = validate.parsePhoneNumberWithCountryCode(primaryContact.phoneNumberWithCountryCode)
            allContacts.push(primaryContact);
          }
          
          // Second, get associated contacts
          try {
            const associatedContactsResponse = await api.get(`/hubspot/get-associated-contacts`,
              {
                params:
                {
                  objectId:recordId,
                  objectType
                }
              }
            );
            
            if (associatedContactsResponse.status === 200) {
              const associatedContacts = await associatedContactsResponse.data;
              if (Array.isArray(associatedContacts)) {
                const validAssociatedContacts = associatedContacts.filter(contact => 
                  contact.name && 
                  contact.email && 
                  // Avoid duplicating the primary contact if it appears in associated contacts
                  !allContacts.some(existing => existing.email.toLowerCase() === contact.email.toLowerCase())
                );
                validAssociatedContacts.forEach((contact)=>{
                  contact.phoneNumberWithCountryCode = validate.parsePhoneNumberWithCountryCode(contact.phoneNumberWithCountryCode);
                });
                allContacts.push(...validAssociatedContacts);
              }
            }
          } catch (associatedError) {
            console.error('Error loading associated contacts for contact object:', associatedError);
            // Don't show error for associated contacts failure if we have primary contact
          }
        } catch (error) {
          console.error('Error loading contact data for contact object:', error);
        }
      } else if (objectType.toLowerCase() === 'deal' || objectType.toLowerCase() === 'company') {
        // For deals and companies, get associated contacts
        try {
          const response = await api.get(`/hubspot/get-associated-contacts`,
            {
              params:{
                objectId:recordId,
                objectType
              }
            }
          );
          
          if (!response.status === 200) {
            const errorText = await response.data;
            let errorMessage = 'Failed to load associated contacts.';
            
            try {
              const errorData = JSON.parse(errorText);
              if (errorData.message) {
                errorMessage = errorData.message;
              }
            } catch (parseError) {
              if (errorText) {
                errorMessage = errorText;
              }
            }
            
            showToaster(errorMessage, 'error');
            return;
          }
          
          const data = await response.data;
          if (Array.isArray(data)) {
            const validContacts = data.filter(contact => contact.name && contact.email);
            validContacts.forEach((contact)=>{
              contact.phoneNumberWithCountryCode = validate.parsePhoneNumberWithCountryCode(contact.phoneNumberWithCountryCode);
            });
            allContacts.push(...validContacts);
          }
        } catch (error) {
          console.error('Error loading associated contacts:', error);
          showToaster('Failed to load contact information. Please try again.', 'error');
          return;
        }
      } 
      
      // Set all collected contacts
      setContactSuggestions(allContacts);
      setFilteredContactSuggestions(allContacts);
    } catch (error) {
      console.error('Error loading contact data:', error);
      showToaster('Unable to load contact information. Please check your connection.', 'error');
    }
  }, [recordId, objectType]);

  // Load contact data when recipients are set and recordId is available
  useEffect(() => {
    if (recordId && objectType) {
      loadContactData();
    }
  }, [recordId, objectType, loadContactData]);

  // Function to filter contact suggestions based on search text
  const filterContactSuggestions = useCallback((searchText) => {
    if (!searchText || searchText.trim() === '') {
      setFilteredContactSuggestions(contactSuggestions);
      return;
    }
    
    const filtered = contactSuggestions.filter(contact => {
      const searchLower = searchText.toLowerCase();
      return contact.name.toLowerCase().includes(searchLower) || 
             contact.email.toLowerCase().includes(searchLower) || 
             contact.phoneNumberWithCountryCode || 
             contact.phoneNumber;
    });
    
    setFilteredContactSuggestions(filtered);
  }, [contactSuggestions]);

  // Function to apply suggestion data to recipient
  const applySuggestion = (recipientId, selectedContact) => {
    if (!selectedContact) return;
    
    const index = recipients.findIndex(r => r.id === recipientId);
    if (index === -1) return;
    
    // If selected contact's email exists in CC, set inline error for recipient email
    let emailError = '';
    let nameError = '';
    if (selectedContact.email && isValidEmail(selectedContact.email)) {
      const ccEmailsLower = ccEmailTags.map(e => e.toLowerCase());
      if (ccEmailsLower.includes(selectedContact.email.toLowerCase())) {
        emailError = 'Recipient already exists in CC';
      }
    }

    if (!selectedContact.name) {
      nameError = 'Name is required';
    }

    setErrors(prev => {
      const newRecipientsErrors = [...(prev.recipients || [])];
      if (!newRecipientsErrors[index]) newRecipientsErrors[index] = {};
      newRecipientsErrors[index].email = emailError;
      return {
        ...prev,
        recipients: newRecipientsErrors
      };
    });

    setErrors(prev => {
      const newRecipientsErrors = [...(prev.recipients || [])];
      if (!newRecipientsErrors[index]) newRecipientsErrors[index] = {};
      newRecipientsErrors[index].name = nameError;
      return {
        ...prev,
        recipients: newRecipientsErrors
      };
    });

    var iso = "us";
    var countryCode = `+1`;
    var nationalNumber = "";
    var fullPhone = "";

    const parsed = parsePhoneNumberFromString(selectedContact.phoneNumberWithCountryCode);
    const isCountryAllowed = ALLOWED_COUNTRIES.includes(parsed?.country?.toLowerCase())

    if (parsed && isCountryAllowed) {
      iso = parsed.country?.toLowerCase();
      countryCode = `+${parsed.countryCallingCode}`;
      nationalNumber = parsed.nationalNumber;
      fullPhone = parsed.number;
    }

    // Update name and email
    const updatedRecipients = recipients.map(recipient => {
      if (recipient.id === recipientId) {
        const canSendEmail = recipient.deliveryOption !== 'SMS';
        const canSendSMS = recipient.deliveryOption !== 'Email';
        const overridePhoneNumer = recipient.authType === 'SMS' && !recipient.phone;

        return {
          ...recipient,
          name: selectedContact.name,
          email: canSendEmail ? selectedContact.email : '',
          fullPhone: canSendSMS ? fullPhone : overridePhoneNumer ? fullPhone : recipient.fullPhone,
          phone: canSendSMS ? nationalNumber : overridePhoneNumer ? nationalNumber : recipient.phone,
          countryCode: canSendSMS ? countryCode : overridePhoneNumer ? countryCode : recipient.countryCode,
          iso: canSendSMS ? parsed?.country?.toLowerCase() : overridePhoneNumer ? parsed?.country?.toLowerCase() : recipient.iso
        };
      }
      return recipient;
    });
    
    setRecipients(updatedRecipients);
    
    // Clear highlight and the focused input after selection
    setHoveredRecipientId(null);
    setFocusedInputId(null);
  };

  // Function to update ccRecipients based on tags
  const updateCcRecipientsFromTags = (tags) => {
    setCcRecipients(tags.join(', '));
  };

  // Handle template selection
  const handleTemplateSelect = async (templateId, templateName) => {
    setSelectedTemplate(templateId);
    setSelectedTemplateName(templateName);
  };

  // Handle close
  const handleClose = () => {
    window.parent.postMessage(JSON.stringify({ action: 'DONE' }), '*');
  };

  // Dropdown and order editing functions (similar to TemplateCreate)
  const finishEditingOrder = useCallback(() => {
    if (editingOrderId === null) return;
    const newOrder = parseInt(tempOrderValue) || 1;
    
    const currentRecipient = recipients.find(r => r.id === editingOrderId);
    
    const currentOrder = currentRecipient ? currentRecipient.order : 1;
    
    if (newOrder === currentOrder) {
      setEditingOrderId(null);
      setTempOrderValue('');
      return;
    }

    const allOrders = recipients.map(r => r.order).sort((a, b) => a - b);
    let maxConsecutive = 0;
    for (let i = 1; i <= recipients.length; i++) {
      if (allOrders.includes(i)) {
        maxConsecutive = i;
      } else {
        break;
      }
    }
    const nextConsecutive = maxConsecutive + 1;
    const finalOrder = newOrder <= nextConsecutive ? newOrder : nextConsecutive;
    const updatedRecipients = recipients.map(recipient =>
      recipient.id === editingOrderId
        ? { ...recipient, order: finalOrder }
        : recipient
    );
    const uniqueOrders = [...new Set(updatedRecipients.map(r => r.order))].sort((a, b) => a - b);
    const orderMapping = {};
    uniqueOrders.forEach((order, index) => {
      orderMapping[order] = index + 1;
    });
    const compressedRecipients = updatedRecipients.map(recipient => ({
      ...recipient,
      order: orderMapping[recipient.order]
    }));
    const sortedRecipients = compressedRecipients.sort((a, b) => {
      if (a.order === b.order) {
        if (a.id === editingOrderId) return -1;
        if (b.id === editingOrderId) return 1;
        return 0;
      }
      return a.order - b.order;
    });
    setRecipients(sortedRecipients);
    setEditingOrderId(null);
    setTempOrderValue('');
  }, [editingOrderId, tempOrderValue, recipients]);

  const toggleDropdown = (type, id) => {
    if (activeDropdown.type === type && activeDropdown.id === id) {
      setActiveDropdown({ type: null, id: null });
    } else {
      setActiveDropdown({ type, id });
    }
  };
 const addRecipient = () => {
  if (recipients.length >= MAX_RECIPIENTS) {
    return;
  }
  const newId = recipients.length > 0 ? Math.max(...recipients.map(r => r.id)) + 1 : 1;

  // Find the next available consecutive order
  const existingOrders = recipients.map(r => r.order).sort((a, b) => a - b);
  let newOrder = 1;
  while (existingOrders.includes(newOrder)) {
    newOrder++;
  }

  // Assign a unique color from the fixed sequence
  const usedColors = Object.values(recipientColors);

  const availableColor = fixedColorSequence.find(color => !usedColors.includes(color)) 
    || fixedColorSequence[recipients.length % fixedColorSequence.length];

    setRecipients([
      ...recipients,
      {
        id: newId,
        roleName: '',
        name: '',
        email: '',
        fullPhone: '',
        phone: '',
        countryCode: '',
        deliveryOption: 'Email',
        language: 'EN',
        role: 'signer',
        order: newOrder,
        isFromTemplate: false,
        allowRoleEdit: true,
        allowRoleDelete: true,
        showDeliveryOptions: false,
        tags: [],
        enablePrintAndSign: false,
        enableReassign: true,
        templateBrandId: '',
        privateMessage: '',
        accessCode: '',
        enableEmailOTP: false,
        enableAccessCode: false,
        showSettingsOptions: false,
        showAuthenticationSettings: false,
        privateMessageSetting: false,
        allowFieldConfigurationSetting: false,
        authType: 'accessCode'
      }
    ]);
    
    // Assign color
    setRecipientColors(prev => ({ ...prev, [newId]: availableColor }));
    
    setErrors(prev => ({
      ...prev,
      recipients: [...(prev.recipients || []), { name: '', email: '', roleName: '', phone: '' }]
    }));
  };

  const removeRecipient = (id) => {
  if (recipients.length > 1) {
    const index = recipients.findIndex(r => r.id === id);
    const removedRecipient = recipients.find(r => r.id === id);

    // Remove the recipient
    const remainingRecipients = recipients.filter(recipient => recipient.id !== id);

    // Normalize signer order to be consecutive starting from 1
    const sortedByOrder = [...remainingRecipients].sort((a, b) => a.order - b.order);
    const normalizedRecipients = sortedByOrder.map((recipient, idx) => ({
      ...recipient,
      order: idx + 1
    }));

    setRecipients(normalizedRecipients);

    // Remove corresponding error and recalculate duplication errors
    setErrors(prev => {
      let newRecipientErrors = [...prev.recipients];
      if (index !== -1) {
        newRecipientErrors.splice(index, 1);
      }

      if (!signingOrder) {
        const emailCount = new Map();
        const phoneCount = new Map();

        normalizedRecipients.forEach(r => {
          const email = (r.email || '').trim().toLowerCase();
          if (email && isValidEmail(email)) {
            emailCount.set(email, (emailCount.get(email) || 0) + 1);
          }
          if (r.deliveryOption === 'SMS' || r.deliveryOption === 'EmailAndSMS') {
            const normalized = validate.normalizePhoneNumber(r.phone);
            if (normalized) {
              phoneCount.set(normalized, (phoneCount.get(normalized) || 0) + 1);
            }
          }
        });

        newRecipientErrors = normalizedRecipients.map((r, i) => {
          const err = { ...(newRecipientErrors[i] || {}) };
          const email = (r.email || '').trim().toLowerCase();
          if (email && emailCount.get(email) <= 1 && err.email === 'Email already exists') {
            err.email = '';
          }
          if (r.deliveryOption === 'SMS' || r.deliveryOption === 'EmailAndSMS') {
            const normalized = validate.normalizePhoneNumber(r.phone);
            if (normalized && phoneCount.get(normalized) <= 1 && err.phone === 'Recipient already exists') {
              err.phone = '';
            }
          }
          return err;
        });
      }
      return {
        ...prev,
        recipients: newRecipientErrors
      };
    });

    // Remove corresponding color
    setRecipientColors(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  }
};

  const updateRecipientPhone = (id, iso, countryCode, phone, fullPhone) => {
    setRecipients(prev =>
      prev.map(r =>
        r.id === id
          ? {
            ...r,
            iso: iso,
            countryCode: countryCode,
            phone: phone || '',
            fullPhone: phone ? fullPhone : countryCode
          }
          : r
      )
    );

    setErrors(prev => {
      const newRecipientErrors = [...prev.recipients];
      
      // We iterate over all recipients to update duplicate status for everyone
      const updatedRecipients = recipients.map(r => r.id === id ? { ...r, phone, countryCode, deliveryOption: r.deliveryOption } : r
      );

      updatedRecipients.forEach((r, idx) => {
        if (!newRecipientErrors[idx]) newRecipientErrors[idx] = { name: '', email: '', roleName: '', phone: '' };

        if (signingOrder) {
          if (newRecipientErrors[idx].phone === 'Recipient already exists') {
            newRecipientErrors[idx].phone = '';
          }
        } else {
          const isDuplicate = updatedRecipients.some((other, otherIdx) => {
            if (idx === otherIdx) return false;
            const normalizedR = validate.normalizePhoneNumber(r.phone);
            const normalizedOther = validate.normalizePhoneNumber(other.phone);
            return (r.deliveryOption === 'SMS' || r.deliveryOption === 'EmailAndSMS') &&
                   (other.deliveryOption === 'SMS' || other.deliveryOption === 'EmailAndSMS') &&
                   normalizedR === normalizedOther &&
                   normalizedR !== '';
          });

          if (isDuplicate) {
            newRecipientErrors[idx].phone = 'Recipient already exists';
          } else if (newRecipientErrors[idx].phone === 'Recipient already exists') {
            newRecipientErrors[idx].phone = '';
          }
        }

        // Basic validation for the field being edited
        if (r.id === id) {
          const normalized = validate.normalizePhoneNumber(phone);
          if (!normalized) {
            newRecipientErrors[idx].phone = 'Phone number is required';
          } else if (!validate.isValidPhoneNumber(countryCode + phone)) {
            newRecipientErrors[idx].phone = 'Please enter a valid phone number';
          }
          else{
            newRecipientErrors[idx].phone = '';
          }
        }
      });

      return { ...prev, recipients: newRecipientErrors };
    });
  };

  const handleDeliveryOptionChange = (recipientId, option) => {
    setRecipients(prev =>
      prev.map(r => {
        if (r.id !== recipientId) return r;

        let updated = {
          ...r,
          deliveryOption: option
        };

        // Determine whether the currently selected authType is still valid
        // for the new delivery option. If not, disable authentication and
        // reset to the default ('accessCode').
        const isEmailOtp = (r.authType || '').toLowerCase() === 'email';
        const isSmsOtp = (r.authType || '').toLowerCase() === 'sms';

        let showAuthenticationSettings = r.showAuthenticationSettings;
        let authType = r.authType;
        if (option === 'SMS') {
          // Email OTP requires Email or EmailAndSMS delivery
          if (isEmailOtp) {
            showAuthenticationSettings = false;
            authType = 'accessCode';
          }
        } else if (option === 'Email') {
          // SMS OTP requires SMS or EmailAndSMS delivery
          if (isSmsOtp) {
            showAuthenticationSettings = false;
            authType = 'accessCode';
          }
        }

        updated.showAuthenticationSettings = showAuthenticationSettings;
        updated.authType = authType;

        if (option === 'SMS' || option === 'EmailAndSMS') {
          if (!r.iso && !r.countryCode) {
            updated.iso = DEFAULT_COUNTRY;
            updated.countryCode = '+1';
            updated.phone = '';
            updated.fullPhone = '';
          }
          if (option === 'SMS') {
            updated.email = '';
          }
        }
        if (option === 'Email') {
          updated.phone = '';
          updated.fullPhone = '';
          updated.countryCode = '';
          
          setErrors(prev => {
            const idx = recipients.findIndex(r => r.id === recipientId);
            if (idx === -1) return prev;
          
            const updatedErrors = [...prev.recipients];
            if (updatedErrors[idx]) {
              updatedErrors[idx].phone = '';
            }
          
            return { ...prev, recipients: updatedErrors };
          });
        }
        return updated;
      })
    );
    setErrors(prev => {
      const idx = recipients.findIndex(r => r.id === recipientId);
      if (idx === -1) return prev;

      const updatedErrors = [...prev.recipients];
      if (!updatedErrors[idx]) updatedErrors[idx] = {};

      if (option === 'SMS') {
        updatedErrors[idx].email = '';
      }

      if (option === 'Email') {
        updatedErrors[idx].phone = '';
      }

      return { ...prev, recipients: updatedErrors };
    });
  };
  const updateRecipient = (id, field, value) => {
    const index = recipients.findIndex(r => r.id === id);
    
    // Create updated recipients list
    const updatedRecipients = recipients.map(recipient =>
      recipient.id === id ? { ...recipient, [field]: value } : recipient
    );

    if (field === 'role' && value === 'reviewer') {
        updatedRecipients.allowFieldConfigurationSetting = false;
    }

    setRecipients(updatedRecipients);

    // If updating the email field, set inline error when this email exists in CC
   if (index !== -1) {
  setErrors(prev => {
    const newRecipientErrors = [...(prev.recipients || [])];

    if (!newRecipientErrors[index]) {
      newRecipientErrors[index] = { name: '', email: '', roleName: '' };
    }

    if (field === 'email') {
      const trimmed = (value || '').trim().toLowerCase();

      if (!trimmed) {
        newRecipientErrors[index].email = 'Email is required';
      } else if (!isValidEmail(trimmed)) {
        newRecipientErrors[index].email = 'Please enter a valid email';
      } else {
        // ✅ Check duplicate among recipients
        const isDuplicate = updatedRecipients.some(
          (r, i) =>
            i !== index &&
            r.email &&
            r.email.trim().toLowerCase() === trimmed
        );

        // ✅ Check duplicate with CC
        const existsInCC = ccEmailTags.some(
          e => e.toLowerCase() === trimmed
        );

        if (isDuplicate) {
          newRecipientErrors[index].email = 'Email already exists';
        } else if (existsInCC) {
          newRecipientErrors[index].email = 'Recipient already exists in CC';
        } else {
          newRecipientErrors[index].email = '';
        }
        if (newRecipientErrors[index] && field === 'accessCode' && value.trim()) {
          newRecipientErrors[index] = { accessCode: '' };
        }
      }
    } else {
      // Clear other field errors normally
      if (newRecipientErrors[index][field]) {
        newRecipientErrors[index][field] = '';
      }
    }

    return { ...prev, recipients: newRecipientErrors };
  });
}

    
    // If updating the name field, filter contact suggestions
    if (field === 'name') {
      filterContactSuggestions(value);
    }
    
    if (index !== -1) {
      setErrors(prev => {
        let newRecipientErrors = [...(prev.recipients || [])];
        if (!newRecipientErrors[index]) {
          newRecipientErrors[index] = { name: '', email: '', roleName: '' };
        }

        if (field === 'email') {
          const trimmed = (value || '').trim();
          const validNow = trimmed && isValidEmail(trimmed);
          if (validNow) {
            const existsInCC = ccEmailTags.some(e => e.toLowerCase() === trimmed.toLowerCase());
            newRecipientErrors[index].email = existsInCC ? 'Recipient already exists in CC' : '';
            if (!signingOrder) {
              const emailValue = trimmed.toLowerCase();
              const emailCount = updatedRecipients.filter(r => (r.email || '').trim().toLowerCase() === emailValue).length;

              if (emailCount > 1) {
                newRecipientErrors[index].email = 'Email already exists';
              } else {
                // If this email is now unique, clear "Email already exists" for all occurrences of this email
                newRecipientErrors = updatedRecipients.map((r, i) => {
                  const err = { ...(newRecipientErrors[i] || {}) };
                  if ((r.email || '').trim().toLowerCase() === emailValue && err.email === 'Email already exists') {
                    err.email = '';
                  }
                  return err;
                });
              }
            }
          }
        } else if (field === 'accessCode') {
            if (value.trim() === '') {
              newRecipientErrors[index] = { ...newRecipientErrors[index], accessCode: 'Access Code is required' };
            } else if (newRecipientErrors[index].accessCode === 'Access code is required') {
              newRecipientErrors[index] = { ...newRecipientErrors[index], accessCode: '' };
            }
        } else {
          // For other fields, clear their error on change as before
          if (newRecipientErrors[index][field]) {
            newRecipientErrors[index][field] = '';
          }
        }

        return { ...prev, recipients: newRecipientErrors };
      });
    }
    
    setActiveDropdown({ type: null, id: null });
  };

  const startEditingOrder = (id, currentOrder) => {
    if (!signingOrder) return;
    setEditingOrderId(id);
    setTempOrderValue(currentOrder.toString());
  };

  const handleOrderChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setTempOrderValue(value);
  };

  const handleOrderKeyPress = (e) => {
    if (e.key === 'Enter') {
      finishEditingOrder();
    }
  };

  const getRoleIcon = (roleId) => {
    const role = recipientRoles.find(r => r.id === roleId);
    return role ? role.icon : <div className='signer-icon'></div>;
  };
  // Display-only conversion for language code: show hyphen in UI, keep underscore in values
  const getDisplayLanguageCode = (code) => {
    if (!code) return '';
    return code.replace('_', '-');
  };

  const isValidEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  // Validate a single recipient's email on blur without requiring Next click
  const validateRecipientEmailField = (recipientId) => {
    const idx = recipients.findIndex(r => r.id === recipientId);
    if (idx === -1) return;
    const email = (recipients[idx].email || '').trim();
    setErrors(prev => {
      const newRecipientErrors = [...(prev.recipients || [])];
      if (!newRecipientErrors[idx]) {
        newRecipientErrors[idx] = { name: '', email: '', roleName: '' };
      }
      if (!email) {
        newRecipientErrors[idx].email = 'Email is required';
      } else if (!isValidEmail(email)) {
        newRecipientErrors[idx].email = 'Please enter a valid email';
      } else {
        const lowerEmail = email.toLowerCase();

        const isDuplicate = recipients.some((r, i) => i !== idx && r.email && r.email.toLowerCase() === lowerEmail);

        const existsInCC = ccEmailTags.some(
          e => e.toLowerCase() === lowerEmail
        );
        if (isDuplicate) {
          newRecipientErrors[idx].email = 'Email already exists';
        } else if (existsInCC) {
          newRecipientErrors[idx].email = 'Recipient already exists in CC';
        } else {
          newRecipientErrors[idx].email = '';
        }
      }
      return { ...prev, recipients: newRecipientErrors };
    });
  };

  const validateCcEmails = (emails) => {
    if (!emails.trim()) return true;
    
    const emailList = emails.split(',').map(e => e.trim());
    return emailList.every(email => isValidEmail(email));
  };

  // Toggle delivery options visibility based on checkbox
  const toggleDeliveryOptions = (recipientId) => {
    setRecipients(prev => prev.map(recipient =>
      recipient.id === recipientId ? { ...recipient, showDeliveryOptions: !recipient.showDeliveryOptions } : recipient
    ));
  };
  // Toggle delivery options visibility based on checkbox
  const toggleOption = (recipientId, type) => {
    setRecipients(prev =>
      prev.map(r => {
        if (r.id !== recipientId) return r;

        if (type === "delivery") {
          return {
            ...r,
            showDeliveryOptions: !r.showDeliveryOptions,
            showSettingsOptions: false
          };
        }

        if (type === "settings") {
          return {
            ...r,
            showSettingsOptions: !r.showSettingsOptions,
            showDeliveryOptions: false
          };
        }

        return r;
      })
    );
  };
  
  const toggleSetting = (id, key) => {
    const index = recipients.findIndex(r => r.id === id);
    const recipient = recipients.find(r => r.id === id);
    if (key === "allowFieldConfigurationSetting" && recipient?.role === "reviewer") {
        return;
      }
    setRecipients(prev =>
      prev.map(r =>
        r.id === id
          ? { ...r, [key]: !r[key] }
          : r
      )
    );
    setErrors(prev => {
      const newRecipientErrors = [...prev.recipients];
      if (newRecipientErrors[index]) {
        newRecipientErrors[index] = { accessCode: '', phone: '' };
      }
      return { ...prev, recipients: newRecipientErrors };
    });
  };
  
  const handleAuthTypeChange = (id, value) => {
    setRecipients(prev =>
      prev.map(r =>
        r.id === id
          ? { ...r, authType: value }
          : r
      )
    );
  };

  const authTypeToShow = (recipient) => {
    if (recipient.authType === 'accessCode') {
      if (!recipient.accessCode) {
        return {
          text: 'Access code enabled but value not filled',
          isError: true
        };
      }
      return {
        text: 'Access Code',
        isError: false
      };
    }

    if (recipient.authType === 'email') {
      return {
        text: 'Email OTP',
        isError: false
      };
    }

    if (recipient.authType === 'SMS') {
      if (!recipient.phone && recipient.deliveryOption === 'Email') {
        return {
          text: 'Please enter a valid phone number',
          isError: true
        };
      }
      return {
        text: 'SMS OTP',
        isError: false
      };
    }

    return { text: '', isError: false };
  };

  useEffect(() => {
    if (!hasIncompleteUploads && errors.progressError) {
      setErrors(prev => ({
        ...prev,
        progressError: ''
      }));
    }
  }, [hasIncompleteUploads, errors.progressError]);

  const updateDocumentInfoField = (locale, field, value, currentInfo) => {
    const title = field === 'title' ? value : currentInfo.title || '';
    const message = field === 'message' ? value : currentInfo.message || '';

    skipDocMirrorRef.current = false;
    if (locale) {
      setDocumentInfo(prev => ({
        ...prev,
        [locale]: {
          ...(prev[locale] || { title: '', message: '' }),
          [field]: value
        }
      }));
    }
    skipDocMirrorRef.current = true;
    setDocuments(prev => ({ ...prev, [field]: value }));
    lastWrittenDocDetailsRef.current = { title, message };
    setDocumentDetails(prev => ({ ...prev, title, message }));

    if (field === 'title' && errors.documentTitle) {
      setErrors(prev => ({ ...prev, documentTitle: '' }));
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      documentTitle: '',
      Files: [],
      recipients: recipients.map(() => ({ name: '', email: '', roleName: '', phone: '' })),
      cc: ''
    };

    if (hasIncompleteUploads) {
        setErrors(prev => ({
          ...prev,
          progressError: 'Some of the uploaded file(s) are not completed.'
        }));
        return;
      }

    const uniqueLocalesValidate = getUniqueRecipientLocales();
    const validatedTitleSource = (uniqueLocalesValidate.length > 1) ? (documentInfo[activeLocale] && documentInfo[activeLocale].title) || '' : (documentInfo[uniqueLocalesValidate[0]] && documentInfo[uniqueLocalesValidate[0]].title) || documentDetails.title || '';
    if (!validatedTitleSource.trim()) {
      newErrors.documentTitle = 'Document title is required';
      isValid = false;
    }
    
    // Ensure at least one signer exists
    if (!recipients.some(r => r.role === 'signer')) {
      newErrors.signerError = 'You need to assign atleast one signer for a document';
      isValid = false;
    } else {
      newErrors.signerError = '';
    }

    // Validate recipients
    recipients.forEach((recipient, index) => {
      // Check for required name
      if (!recipient.name.trim()) {
        newErrors.recipients[index].name = 'Name is required';
        isValid = false;
      }

      // Check email or phone number
      if (recipient.deliveryOption === 'Email' || recipient.deliveryOption === 'EmailAndSMS') {
        if (!recipient.email.trim()) {
          newErrors.recipients[index].email = 'Email is required';
          isValid = false;
        } else if (!isValidEmail(recipient.email)) {
          newErrors.recipients[index].email = 'Please enter a valid email';
          isValid = false;
        }
      }
      if (recipient.deliveryOption == 'SMS' || recipient.deliveryOption === 'EmailAndSMS') {
        const currentPhone = validate.normalizePhoneNumber(recipient.phone);

        if (!currentPhone) {
          newErrors.recipients[index].phone = 'Phone number is required';
          isValid = false;
        } else if (!validate.isValidPhoneNumber(recipient.countryCode + recipient.phone) && recipient.phone) {
          newErrors.recipients[index].phone = 'Please enter a valid phone number';
          isValid = false;
        } else {
          const isDuplicate = recipients.some((r, i) =>
            i !== index &&
            (r.deliveryOption === 'SMS' || r.deliveryOption === 'EmailAndSMS') && 
            validate.normalizePhoneNumber(r.phone) === currentPhone
          );
        
          if (!signingOrder && isDuplicate) {
            newErrors.recipients[index].phone = 'Recipient already exists';
            isValid = false;
          } else {
            newErrors.recipients[index].phone = '';
          }
        }
      }
      // Validate role name for new recipients (not from template)
      if (!recipient.isFromTemplate && !recipient.roleName.trim()) {
        newErrors.recipients[index].roleName = 'Role name is required';
        isValid = false;
      }
      if (recipient.showAuthenticationSettings && recipient.authType === 'SMS' && !recipient.phone) {
        newErrors.recipients[index].phone = !recipient.phone ? 'Phone number is required' : 'Please enter a valid phone number'
        isValid = false;
      }
      if (recipient.showAuthenticationSettings && recipient.authType === 'accessCode' && !recipient.accessCode) {
        newErrors.recipients[index].accessCode = 'Access code is required';
        isValid = false;
      } else{
        newErrors.recipients[index].accessCode = '';
      }
    });

    // Now check for duplicate emails
    const emailsCount = new Map();
    recipients.forEach(r => {
      const email = r.email.trim().toLowerCase();
      if (email) emailsCount.set(email, (emailsCount.get(email) || 0) + 1);
    });

    recipients.forEach((recipient, index) => {
      const email = recipient.email.trim().toLowerCase();
      if (!signingOrder && email && emailsCount.get(email) > 1) {
        if (!newErrors.recipients[index].email) { // Don't override other email errors
          newErrors.recipients[index].email = 'Email already exists';
        }
        isValid = false;
      }
    });

    // Check if any recipient email exists in CC emails
    const ccEmailsLower = ccEmailTags.map(e => e.toLowerCase());
    recipients.forEach((recipient, index) => {
      const email = recipient.email.trim().toLowerCase();
      if (email && ccEmailsLower.includes(email)) {
        newErrors.recipients[index].email = 'Recipient already exists in CC';
        isValid = false;
      }
    });

    // Validate CC field
    if (ccInputValue && !isValidEmail(ccInputValue.trim())) {
      newErrors.cc = 'Please enter a valid email address';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  useEffect(() => {
    if (!hasIncompleteUploads && errors.progressError) {
      setErrors(prev => ({
        ...prev,
        progressError: ''
      }));
    }
  }, [hasIncompleteUploads, errors.progressError]);

  const submitTemplateSend = async () => {
    setIsLoading(true);

    try {
      const formData = new FormData();

      // Add template ID
      formData.append('templateId', selectedTemplate);

      const uniqueLocales = [...new Set(recipients.map(recipient => recipient.language))];
      const firstLocale = uniqueLocales[0];
      const firstLocaleInfo = firstLocale ? (documentInfo[firstLocale] || { title: '', message: '' }) : documentDetails;
      formData.append('title', firstLocaleInfo.title || '');

      // Add every user-uploaded file in the exact order shown in the UI.
      // HubSpot attachments are converted to File objects before submission.
      for (let i = 0; i < uploadedFiles.length; i++) {
        const fileObj = uploadedFiles[i];
        if (fileObj.file) {
          formData.append('files', fileObj.file, fileObj.name);
        }
      }

      // Set redirect URL
      formData.append('redirectUrl', window.location.origin + (isPopup ? '/templateSendSuccess?popup=1' : '/templateSendSuccess'));

      // Enable signing order flag
      formData.append('enableSigningOrder', signingOrder ? 'true' : 'false');

      // Process CC recipients - send as individual ccEmails entries
      if (ccEmailTags.length > 0) {
        ccEmailTags.forEach((email) => {
          formData.append('ccEmails', email);
        });
      }

      // Compute roleRemovalIndices for roles removed from original template
      const currentTemplateIndices = recipients.filter(r => r.isFromTemplate).map(r => r.roleIndex).filter(v => typeof v === 'number');
      const removedIndices = originalTemplateRoleIndices.filter(idx => !currentTemplateIndices.includes(idx));
      removedIndices.forEach(idx => formData.append('roleRemovalIndices', idx));
      // After removal, template role indices are re-numbered 1..n in remaining order.
      const remainingOriginal = originalTemplateRoleIndices.filter(idx => !removedIndices.includes(idx)).sort((a,b) => a-b);
      const reindexMap = {};
      remainingOriginal.forEach((oldIdx, i) => { reindexMap[oldIdx] = i + 1; });

      let newRoleIndex = 1;
      // Add roles (recipients)
      recipients.forEach((recipient, index) => {
        formData.append(`roles[${index}].signerName`, recipient.name);
        formData.append(`roles[${index}].locale`, recipient.language);
        formData.append(`roles[${index}].signerType`, recipient.role);
        formData.append(`roles[${index}].signerRole`, recipient.roleName);

        // Delivery Mode (Email or SMS or EmailAndSMS)
        formData.append(`roles[${index}].deliveryMode`,recipient.deliveryOption);

        // Send Email if delivery option is Email or EmailAndSMS
        if (recipient.deliveryOption === 'Email') {
          formData.append(`roles[${index}].signerEmail`, recipient.email);
        } else if (recipient.deliveryOption === 'SMS') {
          formData.append(`roles[${index}].PhoneNumber.number`, recipient.phone);
          formData.append(`roles[${index}].PhoneNumber.countryCode`, recipient.countryCode);
        } else {
          formData.append(`roles[${index}].signerEmail`, recipient.email);
          formData.append(`roles[${index}].PhoneNumber.number`, recipient.phone);
          formData.append(`roles[${index}].PhoneNumber.countryCode`, recipient.countryCode);
        }

        if (recipient.isFromTemplate && typeof recipient.roleIndex === 'number') {
          formData.append(`roles[${index}].roleIndex`, recipient.roleIndex);
        } else {
          formData.append(`roles[${index}].roleIndex`, templateRolesLength+newRoleIndex);
          newRoleIndex++;
        }
        if (signingOrder) {
          formData.append(`roles[${index}].signerOrder`, recipient.order || index + 1);
        }
        if (recipient.showAuthenticationSettings) {
          if (recipient.authType === 'SMS') {
            formData.append(`roles[${index}].authenticationType`, 'SMSOTP');
            if (recipient.deliveryOption !== 'SMS' || recipient.deliveryOption === 'EmailAndSMS') {
              formData.append(`roles[${index}].phoneNumber.number`, recipient.phone);
              formData.append(`roles[${index}].phoneNumber.countryCode`, recipient.countryCode);
            }
          }
          if (recipient.authType === 'email') {
            formData.append(`roles[${index}].authenticationType`, 'EmailOTP');
            formData.append(`roles[${index}].enableEmailOTP`, true);
          }
          if (recipient.authType === 'accessCode') {
            formData.append(`roles[${index}].authenticationType`, 'AccessCode');
            formData.append(`roles[${index}].authenticationCode`, recipient.accessCode);
          }
        }
        if (recipient.privateMessageSetting) {
          formData.append(`roles[${index}].privateMessage`, recipient.privateMessageSettings ? recipient.privateMessage : '');
        }
        if (recipient.allowFieldConfigurationSetting) {
          formData.append(`roles[${index}].allowFieldConfiguration`, recipient.role === 'reviewer' ? false : recipient.allowFieldConfigurationSetting);
        }
      });

      // Then create DocumentInfo entries for each unique locale
      uniqueLocales.forEach((locale, index) => {
        const info = documentInfo[locale] || { title: documentDetails.title, message: documentDetails.message };
        formData.append(`documentInfo[${index}].locale`, locale);
        formData.append(`documentInfo[${index}].title`, info.title || '');
        if (info.message && info.message.trim() !== '' && uniqueLocales.length > 1) {
          formData.append(`documentInfo[${index}].description`, info.message);
        }
      });

      // Add template files information
      templateFiles.forEach((file, index) => {
        formData.append(`templateFiles[${index}].fileName`, file.fileName);
        formData.append(`templateFiles[${index}].fileSize`, file.fileSize);
        formData.append(`templateFiles[${index}].mimeType`, file.mimeType);
        formData.append(`templateFiles[${index}].fileUrl`, file.fileUrl);
        formData.append(`templateFiles[${index}].documentId`, file.documentId);
      });

      // Send recordId and objectType
      formData.append('recordId', recordId);
      formData.append('objectType', objectType);

      // Document settings
      if (documentSettings.brand && documentSettings.brand.id) {
        formData.append('brandId', documentSettings.brand.id);
      }
      formData.append('documentDownloadOption', documentSettings.documentDownloadOption);
      formData.append('enablePrintAndSign', documentSettings.enablePrintAndSign);
      formData.append('enableReassign', documentSettings.enableReassign);
      formData.append('disableExpiryAlert', !documentSettings.enableExpiryAlert);
      formData.append('expiryDateType', 'Days');
      formData.append('expiryValue', parseInt(documentSettings.expiryValue, 10));
      formData.append('reminderSettings.enableAutoReminder', documentSettings.enableAutoReminder);
      formData.append('reminderSettings.reminderDays', documentSettings.enableAutoReminder ? parseInt(documentSettings.reminderDays, 10) : '');
      formData.append('reminderSettings.reminderCount', documentSettings.enableAutoReminder ? parseInt(documentSettings.reminderCount, 10) : '');
      formData.append('labels', documentSettings.tags);

      // Send to API
      const response = await api.post('/boldsign/send-template', formData);

      if (response.status !== 200) {
        const errorData = response?.data;
        if (errorData?.error === 'BoldSignApiError') {
          showToaster(errorData.message, 'error');
        } else {
          setErrorPageConfig({
            title: errorContent.sendRequest.title,
            message: errorContent.sendRequest.message
          });
          setShowErrorPage(true);
        }
        return;
      }

      const sendUrl = response.data;
      if (sendUrl) {
        setIframeSrc(sendUrl);
        setCurrentStep(2);
      } else {
        setErrorPageConfig({
          title: errorContent.sendRequest.title,
          message: errorContent.sendRequest.message
        });
        setShowErrorPage(true);
      }
    } catch (error) {
      console.error('Error sending template:', error);
      // Show standardized Send Request Failed error page for client-side/network failures
      setErrorPageConfig({
        title: errorContent.sendRequest.title,
        message: errorContent.sendRequest.message
      });
      setShowErrorPage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendTemplate = async () => {
    if (isLoading) return;

    // First check if required URL parameters are available
    if (!recordId || !objectType) {
      showToaster("This page works only within HubSpot. If you're in HubSpot, try closing and reopening. Still not working? Reconnect or contact your admin.", 'error');
      return;
    }

    if (hasIncompleteUploads) {
      setErrors(prev => ({
        ...prev,
        progressError: 'Some of the uploaded file(s) are not completed.'
      }));
      return;
    }

    if (validateForm()) {
      // When more than one unique locale is present and message editing is
      // allowed, prompt the user before proceeding if any locale has an empty
      // message.
      const missingLocales = getLocalesWithMissingMessage();
      if (missingLocales.length > 0) {
        setMissingMessageLocales(missingLocales);
        setShowLocaleMessageDialog(true);
        return;
      }

      await submitTemplateSend();
    }
  };

  const closeLocaleMessageDialog = () => {
    setShowLocaleMessageDialog(false);
    setMissingMessageLocales([]);
  };
  const handleLocaleMessageDialogYes = async () => {
    setShowLocaleMessageDialog(false);
    setMissingMessageLocales([]);
    await submitTemplateSend();
  };

  // Handle error page button click
  const handleErrorPageButtonClick = () => {
    // Send DONE message to parent to close the modal/iframe
    try {
      window.parent.postMessage(JSON.stringify({ action: "DONE" }), "*");
    } catch (error) {
      console.error("Error occurred when return to HubSpot:", error);
      // Fallback: try to go back in history
      if (window.history.length > 1) {
        window.history.back();
      }
    }
  };

  // Show error page if needed
  if (showErrorPage) {
    return (
      <Error
        title={errorPageConfig.title}
        message={errorPageConfig.message}
        buttonText={errorPageConfig.buttonText}
        onButtonClick={handleErrorPageButtonClick}
      />
    );
  }

  return (
    <div className={styles.documentSendContainer}>
      {/* Toaster Component */}
      <Toaster
        show={toasterMessage.show}
        message={toasterMessage.message}
        type={toasterMessage.type}
        onClose={hideToaster}
        autoHide={true}
        autoHideDelay={3000}
      />

      {/* Locale message missing confirmation dialog */}
      <MissingLocaleMessageDialogBox
        show={showLocaleMessageDialog}
        localeCodes={missingMessageLocales}
        languageNames={languageNames}
        onClose={closeLocaleMessageDialog}
        onNo={closeLocaleMessageDialog}
        onYes={handleLocaleMessageDialogYes}
      />

      {/* Step 2: Template Configuration */}
      {currentStep === 1 && (
        <>
          <div className={styles.stepNavigationBar}>
            <div className={styles.stepIndicator}>
               Prepare document for signing<span className={styles.stepCount}>(Step 1/2)</span>
            </div>
            <div className="primary-btn-container">
              <button
                className="primary-button next-btn"
                onClick={handleSendTemplate}
                disabled={isLoading || !selectedTemplate}
              >
              Next
              </button>
            </div>
            {isLoading && (
              <div className="loading-overlay">
                <img src={loadIcon} alt="loading" className="spinner-circle-page"/>
              </div>
            )}
          </div>
          <div className={styles.documentContent}>
            {/* Template Details Section - Read Only */}
            <div className="contentSection">
              <div className="contentBox">
                <div className="contentBoxHeader">Template Details</div>
                <div className={styles.templateDetailsSection}>
                  <div className="field-group">
                    <label htmlFor="template-title">Title</label>
                    <input
                      id="template-title"
                      type="text"
                      className="document-input disabled"
                      value={templateDetails.title}
                      disabled
                      readOnly
                    />
                  </div>
                  <div className="field-group">
                    <label htmlFor="template-description">Description</label>
                    <textarea
                      id="template-description"
                      className="document-textarea disabled"
                      value={templateDetails.description}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>

            {allowNewFiles && (<div className="contentSection">
              <FileUploadManager
                mode="templateSend"
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
                uploadingFiles={uploadingFiles}
                setUploadingFiles={setUploadingFiles}
                documents={documents}
                setDocuments={setDocuments}
                errors={errors}
                setErrors={setErrors}
                api={api}
                popup={popup}
                recordId={recordId}
                objectType={objectType}
                showToaster={showToaster}
                allowNewFiles={allowNewFiles}
                allowModifyFiles={allowModifyFiles}
                templateFiles={templateFiles}
              />
            </div>)}

            <div className="contentSection">
              <div className="contentBox">
                <div className="recipient-header">
                  <div className="contentBoxHeader">Add Recipients</div>
                  {<div className="signing-order-toggle">
                    {documentDetails.allowNewRoles && ((addMeVisible || signingOrder) && (
                      <>
                        <button
                          className={styles.addMeLink}
                          onClick={handleAddMeClick}
                          aria-label="Add Me as recipient"
                        >
                          Add Me
                        </button>
                        <svg className={styles.separatorLine} viewBox="0 0 40 40" aria-hidden="true">
                          <line x1="20" y1="0" x2="20" y2="40" className={styles.line} />
                        </svg>
                      </>
                    ))}
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={signingOrder}
                        onChange={() => {
                            const newSigningOrder = !signingOrder;
                            setSigningOrder(newSigningOrder);

                            setErrors(prev => {
                              const newRecipientErrors = [...prev.recipients];

                              if (newSigningOrder) {
                                newRecipientErrors.forEach((err, idx) => {
                                  if (!err) return;

                                  if (err.email === 'Email already exists') {
                                    newRecipientErrors[idx] = { ...err, email: '' };
                                  }

                                  if (err.phone === 'Recipient already exists') {
                                    newRecipientErrors[idx] = { ...err, phone: '' };
                                  }
                                });

                              } else {
                                const emailMap = new Map();
                                const phoneMap = new Map();

                                recipients.forEach((r, idx) => {

                                  const email = (r.email || '').trim().toLowerCase();
                                  if (email && isValidEmail(email)) {
                                    emailMap.set(email, [...(emailMap.get(email) || []), idx]);
                                  }

                                  if (
                                    r.deliveryOption === 'SMS' ||
                                    r.deliveryOption === 'EmailAndSMS'
                                  ) {
                                    const normalized = validate.normalizePhoneNumber(r.phone);
                                    const fullPhone = (r.countryCode || '') + (r.phone || '');

                                    if (normalized && validate.isValidPhoneNumber(fullPhone)) {
                                      phoneMap.set(normalized, [...(phoneMap.get(normalized) || []), idx]);
                                    }
                                  }
                                });

                                emailMap.forEach(indices => {
                                  if (indices.length > 1) {
                                    indices.forEach(i => {
                                      if (!newRecipientErrors[i]) newRecipientErrors[i] = {};
                                      newRecipientErrors[i].email = 'Email already exists';
                                    });
                                  }
                                });

                                phoneMap.forEach(indices => {
                                  if (indices.length > 1) {
                                    indices.forEach(i => {
                                      if (!newRecipientErrors[i]) newRecipientErrors[i] = {};
                                      newRecipientErrors[i].phone = 'Recipient already exists';
                                    });
                                  }
                                });
                              }

                              return { ...prev, recipients: newRecipientErrors };
                            });
                        }}
                        aria-label="Signing Order"
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span>Signing Order</span>
                    <button className="help-button">
                      <span className = {`${styles.iconBase} ${styles.signingOrderInfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="so-tooltip"></span>
                    </button>
                    <Tooltip
                      id="so-tooltip"
                      place="top"
                      className='tooltip-info'
                      render={() => (
                      <div className="tooltip-content">
                          Enable this option to specify signing order for recipients.
                        </div>
                      )}
                    />
                  </div>}
                </div>
                {errors.signerError && <div className="error-message">{errors.signerError}</div>}
                <div className={styles.recipientsList}>
                  {recipients.map((recipient, index) => (
                  <div
                      key={recipient.id}
                      ref={el => recipientRowRefs.current[recipient.id] = el}
                      className={`recipient-data ${signingOrder ? 'so-enabled' : ''} ${dragOverId === recipient.id ? 'drag-over' : ''} ${draggingId === recipient.id ? 'dragging' : ''} ${hoveredRecipientId === recipient.id ? styles.recipientHoverHighlight : ''} ${styles.recipientRow}`}
                      onMouseEnter={() => setHoveredRecipientId(recipient.id)}
                      onMouseLeave={() => setHoveredRecipientId(null)}
                      draggable={signingOrder}
                      onDragStart={(e) => onDragStartHandle(e, recipient.id)}
                      onDragEnd={onDragEndHandle}
                      onDragOver={(e) => onDragOverRow(e, recipient.id)}
                      onDrop={(e) => onDropRow(e, recipient.id)}
                      onDragEnter={() => setDragOverId(recipient.id)}
                      onDragLeave={() => setDragOverId(null)}
                    >
                    <div className={styles.recipientDataSoEnabled}>
                    {signingOrder && (
                      <div
                        className={styles.dragHandle}
                        draggable={true}
                        onDragStart={(e) => onDragStartHandle(e, recipient.id)}
                        onDragEnd={onDragEndHandle}
                        title="Drag to reorder"
                      >
                        <span className={`${styles.kebabIconLeft} sfIconBase sfKebabIcon`}></span>
                        <span className={`${styles.kebabIconRight} sfIconBase sfKebabIcon`}></span>
                      </div>
                    )}
                      <div
                        className={`recipient-number ${signingOrder ? 'editable' : ''}`}
                        onClick={() => signingOrder && startEditingOrder(recipient.id, recipient.order)}
                        onKeyDown={(e) => {
                          if (!signingOrder) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            startEditingOrder(recipient.id, recipient.order);
                          }
                        }}
                        role={signingOrder ? 'button' : undefined}
                        tabIndex={signingOrder ? 0 : -1}
                        style={{ backgroundColor: recipientColors[recipient.id] }}
                      >
                        {editingOrderId === recipient.id ? (
                          <input
                            ref={orderInputRef}
                            type="text"
                            value={tempOrderValue}
                            onChange={handleOrderChange}
                            onKeyDown={handleOrderKeyPress}
                            onBlur={finishEditingOrder}
                            className="order-input"
                          />
                        ) : (
                          recipient.order
                        )}
                      </div>
                      </div>
                      <div className={styles.recipientGroup}>
                        <div className={styles.recipientTopRow}>
                          <div className={`${styles.roleInputGroup} ${styles.floatingLabelWrapper}`}>
                            {(focusedInputId === `role-${recipient.id}` || (recipient.roleName && recipient.roleName.trim() !== '') || recipient.isFromTemplate) && (
                              <span className={`${styles.floatingLabel} ${focusedInputId === `role-${recipient.id}` ? styles.floatingLabelActive : styles.floatingLabelStatic}`}>
                                {recipient.isFromTemplate ? 'Role name*' : 'Role name*'}
                              </span>
                            )}
                            <input
                              type="text"
                              placeholder={focusedInputId === `role-${recipient.id}` ? '' : (recipient.isFromTemplate ? 'Role name*' : 'Enter role*')}
                              className={`recipient-input role-input ${recipient.isFromTemplate ? 'disabled' : ''} ${errors.recipients[index] && errors.recipients[index].roleName ? 'error' : ''}`}
                              value={recipient.roleName}
                              onChange={(e) => !recipient.isFromTemplate && updateRecipient(recipient.id, 'roleName', e.target.value)}
                              onFocus={() => setFocusedInputId(`role-${recipient.id}`)}
                              onBlur={() => setFocusedInputId(null)}
                              disabled={recipient.isFromTemplate}
                              readOnly={recipient.isFromTemplate}
                            />
                          </div>
                          <div className="recipient-actions">
                            <div
                              className="custom-dropdown"
                              ref={el => dropdownRefs.current[`role-${recipient.id}`] = el}
                            >
                              <button
                                type="button"
                                className={`dropdown-toggle-role ${!recipient.allowRoleEdit ? 'disabled' : ''}`}
                                onClick={() => toggleDropdown('role', recipient.id)}
                                disabled={!recipient.allowRoleEdit}
                              >
                                <span className={`${styles.iconBase} role-icon`}>{getRoleIcon(recipient.role)}</span>
                                <span className = {`${styles.dropDownIcon} sfIconBase sfDropDownIcon`}></span>
                              </button>
                              {activeDropdown.type === 'role' && activeDropdown.id === recipient.id && recipient.allowRoleEdit && (
                                <div className="dropdown-menu" style={{ display: 'block', position: 'absolute', zIndex: 1000 }}>
                                  {recipientRoles.map(role => (
                                    <div
                                      key={role.id}
                                      className={`dropdown-item ${recipient.role === role.id ? 'selected' : ''}`}
                                      onClick={() => recipient.allowRoleEdit && updateRecipient(recipient.id, 'role', role.id)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          updateRecipient(recipient.id, 'role', role.id);
                                        }
                                      }}
                                      role="button"
                                      tabIndex={0}
                                    >
                                      <span className="role-icon">{role.icon}</span>
                                      {role.name}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div
                              className="custom-dropdown"
                              ref={el => dropdownRefs.current[`language-${recipient.id}`] = el}
                            >
                              <button
                                type="button"
                                className={`dropdown-toggle-language ${!recipient.allowRoleEdit ? 'disabled' : ''}`}
                                onClick={() => toggleDropdown('language', recipient.id)}
                                disabled={!recipient.allowRoleEdit}
                              >
                                <span className={`${styles.iconBase} language-code`}>{getDisplayLanguageCode(recipient.language)}
                                  <span className = {`${styles.dropDownIcon} sfIconBase sfDropDownIcon`}></span>
                                </span>
                                
                              </button>

                              {activeDropdown.type === 'language' && activeDropdown.id === recipient.id && recipient.allowRoleEdit && (
                                <div className="dropdown-menu" style={{ display: 'inline', position: 'absolute', zIndex: 10, maxHeight: '200px', overflowY: 'auto' }}>
                                  {Object.entries(languageNames).map(([code, name]) => (
                                    <div
                                      key={code}
                                      className={`dropdown-item ${recipient.language === code ? 'selected' : ''}`}
                                      onClick={() => recipient.allowRoleEdit && updateRecipient(recipient.id, 'language', code)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          updateRecipient(recipient.id, 'language', code);
                                        }
                                      }}
                                      role="button"
                                      tabIndex={0}
                                    >
                                      {name} <span className="language-code">({getDisplayLanguageCode(code)})</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className={styles.roleError}>
                          {errors.recipients[index] && errors.recipients[index].roleName && (
                            <div className="input-error">{errors.recipients[index].roleName}</div>
                          )}
                        </div>

                        <div className={styles.recipientDetails}>
                          <div className={`${styles.recipientName} ${styles.floatingLabelWrapper}`} style={{ position: 'relative' }}>
                            {(focusedInputId === `name-${recipient.id}` || (recipient.name && recipient.name.trim() !== '')) && (
                              <span className={`${styles.floatingLabel} ${focusedInputId === `name-${recipient.id}` ? styles.floatingLabelActive : styles.floatingLabelStatic}`}>Recipient name*</span>
                            )}
                            <input
                              type="text"
                              placeholder={focusedInputId === `name-${recipient.id}` ? '' : 'Recipient name*'}
                              className={`recipient-input ${!recipient.allowRoleEdit ? 'disabled' : ''} ${errors.recipients[index] && errors.recipients[index].name ? 'error' : ''}`}
                              value={recipient.name || ''}
                              onChange={(e) => recipient.allowRoleEdit && updateRecipient(recipient.id, 'name', e.target.value)}
                              onFocus={() => {
                                setFocusedInputId(`name-${recipient.id}`);
                                filterContactSuggestions(recipient.name || '');
                              }}
                              onBlur={() => setFocusedInputId(null)}
                              disabled={!recipient.allowRoleEdit}
                              readOnly={!recipient.allowRoleEdit}
                            />
                            {focusedInputId === `name-${recipient.id}` && filteredContactSuggestions.length > 0 && (
                              <div className="contact-suggestions-container">
                                {filteredContactSuggestions.map((contact, contactIndex) => (
                                  <div
                                    key={contactIndex}
                                    className="contact-suggestion-box"
                                    onClick={() => applySuggestion(recipient.id, contact)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        applySuggestion(recipient.id, contact);
                                      }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                  >
                                    <div className="contact-name">{contact.name}</div>
                                    <div className="contact-email-phoneNumber">
                                      <span className="contact-email">
                                        {contact.email}
                                      </span>
                                        {contact.phoneNumberWithCountryCode !== '' && (<span className="contact-separator">|</span>)}
                                      <span className="contact-phone">
                                        {contact.phoneNumberWithCountryCode}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {errors.recipients[index] && errors.recipients[index].name && (
                              <span className="input-error">{errors.recipients[index].name}</span>
                            )}
                          </div>
                          {( recipient.deliveryOption === 'Email' || recipient.deliveryOption === 'EmailAndSMS' ) && (
                          <div className={`${styles.recipientEmail} ${styles.floatingLabelWrapper}`}>
                            {(focusedInputId === `email-${recipient.id}` || (recipient.email && recipient.email.trim() !== '')) && (
                              <span className={`${styles.floatingLabel} ${focusedInputId === `email-${recipient.id}` ? styles.floatingLabelActive : styles.floatingLabelStatic}`}>Recipient email*</span>
                            )}
                            <input
                              type="email"
                              placeholder={focusedInputId === `email-${recipient.id}` ? '' : 'Recipient email*'}
                              className={`recipient-input ${!recipient.allowRoleEdit ? 'disabled' : ''} ${errors.recipients[index] && errors.recipients[index].email ? 'error' : ''}`}
                              value={recipient.email}
                              onChange={(e) => recipient.allowRoleEdit && updateRecipient(recipient.id, 'email', e.target.value)}
                              onFocus={() => setFocusedInputId(`email-${recipient.id}`)}
                              onBlur={() => { validateRecipientEmailField(recipient.id); setFocusedInputId(null); }}
                              disabled={!recipient.allowRoleEdit}
                              readOnly={!recipient.allowRoleEdit}
                            />
                            {errors.recipients[index] && errors.recipients[index].email && (
                              <span className="input-error">{errors.recipients[index].email}</span>
                            )}
                          </div>
                          )}
                          {(recipient.deliveryOption === 'SMS' || recipient.deliveryOption === 'EmailAndSMS') && (
                            <InternationalPhoneInput
                              value={recipient.fullPhone}
                              iso={recipient.iso}
                              nationalNumber={recipient.phone}
                              error={errors.recipients[index]?.phone}
                              styles={styles}
                              disabled={!recipient.allowRoleEdit}
                              showExample={recipient}
                              containerClassName={`template-recipient-phone`}
                              showHelperText={true}
                              placeholderMode = 'TemplateSendExampleNumber'
                              onChange={({ iso, countryCode, nationalNumber, fullPhone }) => {
                                if (!recipient.allowRoleEdit) return
                                updateRecipientPhone(
                                  recipient.id,
                                  iso,
                                  countryCode,
                                  nationalNumber,
                                  fullPhone
                                );
                              }}
                              onFocus={() => setFocusedInputId(`phone-${recipient.id}`)}
                              onBlur={() => {
                                setFocusedInputId(null);
                              }}
                              inputProps={{
                                required: true,
                              }}
                            />
                          )}
                        </div> 
                        {/* Delivery Options */}
                        <div className={`${styles.deliveryOptionButton}`}>
                          <label
                          aria-label='Select Delivery Option'
                          aria-disabled={!recipient.allowRoleEdit}
                          >
                            <input
                              type="checkbox"
                              className={styles.deliveryOptionInput}
                              checked={recipient.showDeliveryOptions}
                              onChange={() => recipient.allowRoleEdit && toggleOption(recipient.id, "delivery")}
                              disabled={!recipient.allowRoleEdit}
                            />
                            <div className={`${styles.deliveryOption}  ${!recipient.allowRoleEdit ? styles.deliveryOptionButtonDisabled : ''}`} style={!recipient.allowRoleEdit ? { cursor: 'not-allowed' } : undefined}>
                              <span className={`${styles.sfIconBase} ${styles.sfSendNowIcon}`} style={{ fontSize: "15px" }}></span>
                              <span className={styles.deliveryOptionText}>{recipient.deliveryOption === 'SMS' ? 'SMS' : recipient.deliveryOption === 'Email' ? 'Email' : 'Email & SMS'}</span>
                              <span className={recipient.showDeliveryOptions ? `${styles.sfIconBase} ${styles.sfWrapUp}` : `${styles.sfIconBase} ${styles.sfDropDown}`}></span>
                            </div>
                          </label>
                          {/* Settings Option */}
                          <label aria-label="Settings">
                            <input
                              type ="checkbox"
                              className = {styles.settingsOptionInput}
                              checked = {recipient.showSettingsOptions}
                              onChange = {() => toggleOption(recipient.id, "settings")}
                            />
                            <div className={styles.deliveryOption}>
                              <span className={`${styles.sfIconBase} ${styles.sfGearIcon} sfIconBase sfGearIcon`} style={{ fontSize: "15px" }}></span>
                              <span className={styles.deliveryOptionText}>Settings</span>
                              <span className={recipient.showSettingsOptions  ? `${styles.sfIconBase} ${styles.sfWrapUp}`  : `${styles.sfIconBase} ${styles.sfDropDown}`}></span>
                            </div>
                          </label>
                          {!recipient.showSettingsOptions && recipient.showAuthenticationSettings && (() => {
                            const result = authTypeToShow(recipient);
                          
                            return (
                              <div className={styles.knowAuth}>
                                <span className={`${styles.sfIconBase} ${styles.sfTickIcon} ${result.isError ? styles.sfTickIconError : ''}  sfIconBase sfTickIcon`}></span>
                                <span className= {`${result.isError ? styles.authError : ''}`}>{result.text}</span>
                              </div>
                            );
                          })()}
                        </div>
                        {recipient.showDeliveryOptions && (
                          <div className={styles.deliveryOptionInfo}>
                            <div className={styles.TitleContainer}>
                              <span className={styles.deliveyOptionTitle}>Delivery Option </span>
                              <button className={styles.helpButton}>
                                <span className = {`${styles.deliveryOptionInfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="do-tooltip"></span>
                              </button>
                              <Tooltip
                                id="do-tooltip"
                                place="right"
                                className='tooltip-info tip-delivery-options'
                                clickable
                                render={() => (
                                  <div className="tooltip-content">
                                    Choose a delivery mode for notifications. For more<br /> information, see <a href='https://support.boldsign.com/kb/article/13965/send-signature-requests-via-sms' target="_blank" rel="noopener noreferrer" className={styles.tooltipLink}>delivery options<span className={styles.sfIconOpenLink}></span></a>.
                                  </div>
                                )}
                              />
                            </div>
                            <div className={styles.deliveryOptionsContainer}>
                              <label className={styles.deliveryOptions}>
                                <input
                                  type="radio"
                                  name={`deliveryOption-${recipient.id}`}
                                  id={`emailDelivery-${recipient.id}`}
                                  value="Email"
                                  checked={recipient.deliveryOption === 'Email'}
                                  onChange={() => handleDeliveryOptionChange(recipient.id, 'Email')}
                                  className={styles.radioIcon}
                                />
                                <span className = {styles.availableDeliveyOptions}>Email</span>
                              </label>
                              <label className={styles.deliveryOptions}>
                                <input
                                  type="radio"
                                  name={`deliveryOption-${recipient.id}`}
                                  id={`smsDelivery-${recipient.id}`}
                                  value="SMS"
                                  checked={recipient.deliveryOption === 'SMS'}
                                  onChange={() => handleDeliveryOptionChange(recipient.id, 'SMS')}
                                  className={styles.radioIcon}
                                />
                                <span className = {styles.availableDeliveyOptions}>SMS</span>
                              </label>
                              <label className={styles.deliveryOptions}>
                                <input
                                type="radio"
                                name={`deliveryOption-${recipient.id}`}
                                id={`emailAndSmsDelivery-${recipient.id}`}
                                value="EmailAndSMS"
                                checked={recipient.deliveryOption === 'EmailAndSMS'}
                                onChange={() => handleDeliveryOptionChange(recipient.id, 'EmailAndSMS')}
                                className={styles.radioIcon}
                                />
                                <span className = {styles.availableDeliveyOptions}>Email & SMS</span>
                              </label>
                            </div>
                          </div>
                        )}
                        {recipient.showSettingsOptions && (
                        <div className={styles.deliveryOptionInfo}>
                          <div className={styles.settingsOptionsContainer}>
                            {/* Toggle 1 */}
                            <div className={styles.availableSettings}>
                              <label className={`toggle-switch`} aria-label="Settings Toggle">
                                <input
                                  type="checkbox"
                                  checked={recipient.showAuthenticationSettings}
                                  onChange={() => toggleSetting(recipient.id, "showAuthenticationSettings")}
                                />
                                <span className={`${styles.toggleSlider} toggle-slider`}></span>
                              </label>
                              <span className= {styles.settingsOption}>Authentication</span>
                              <button className="help-button">
                                <span className = {`${styles.iconBase} ${styles.authenticationinfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="auth-tooltip"></span>
                              </button>
                              <Tooltip
                                id="auth-tooltip"
                                place="top"
                                className='tooltip-authInfo'
                                render={() => (
                                  <div className="tooltip-content">
                                    Recipients will be required to enter a system-generated one-time password or a sender-specified access code to view and sign the document.
                                  </div>
                                )}
                              />
                            </div>
                            {/* CHILD OPTIONS */}
                            {recipient.showAuthenticationSettings && (
                              <div className={styles.settingsChildren}>
                                <label className={styles.radioOption}>
                                  <input 
                                    type = "radio"
                                    name = {`auth-${recipient.id}`}
                                    checked = {recipient.authType === 'email' && recipient.deliveryOption !== 'SMS'}
                                    onChange={() => handleAuthTypeChange(recipient.id, "email")}
                                    className = {styles.radioIcon}
                                    disabled = {recipient.deliveryOption === 'SMS'}
                                    />
                                  <span className={recipient.deliveryOption === 'SMS' ? styles.disableEmailOTP : ''}>Email OTP (One Time Password)</span>
                                </label>
                            
                                <label className={styles.radioOption}>
                                  <input
                                    type="radio"
                                    name = {`auth-${recipient.id}`}
                                    checked = {recipient.authType === 'SMS'}
                                    onChange={() => handleAuthTypeChange(recipient.id, "SMS")}
                                    className={styles.radioIcon}
                                  />
                                  <span>SMS OTP</span>
                                </label>
                                {recipient.authType === "SMS" && (recipient.deliveryOption === 'Email') && (
                                  <div className={styles.SMSSection}>
                                    {/* Phone Input */}
                                    <InternationalPhoneInput
                                      value={recipient.fullPhone}
                                      containerClassName={`recipient-phone`}
                                      iso={recipient.iso}
                                      nationalNumber={recipient.phone}
                                      error={errors.recipients[index]?.phone}
                                      showExample={recipient}
                                      styles={styles}
                                      onChange={({ iso, countryCode, nationalNumber, fullPhone }) => {
                                        updateRecipientPhone(
                                          recipient.id,
                                          iso,
                                          countryCode,
                                          nationalNumber,
                                          fullPhone
                                        );
                                      }}
                                      onFocus={() => setFocusedInputId(`phone-${recipient.id}`)}
                                      onBlur={() => {
                                        setFocusedInputId(null);
                                      }}
                                      placeholderMode = 'ExampleNumber'
                                    />
                                    {/* Note text */}
                                    <div className={styles.smsNote}>
                                      Note: OTP will be sent to the recipient via SMS. A fee will be charged per SMS sent.
                                    </div>
                                  </div>
                                )}
                                <label className={styles.radioOption}>
                                  <input
                                    type="radio"
                                    name = {`auth-${recipient.id}`}
                                    checked = {recipient.authType === 'accessCode' || recipient.authType === 'none'}
                                    onChange={() => handleAuthTypeChange(recipient.id, "accessCode")}
                                    className={styles.radioIcon}
                                  />
                                  <span>Access code</span>
                                </label>
                                <div className={styles.codeSection}>
                                  {recipient.authType === 'accessCode' && (
                                    <div>
                                      <div className={`recipient-accessCode ${styles.floatingLabelWrapper}`} style={{ position: 'relative', width: '100%' }}>
                                        {(focusedInputId === `accessCode-${recipient.id}` || (recipient.accessCode && recipient.accessCode.trim() !== '')) && (
                                          <span className={`${styles.floatingLabel} ${focusedInputId === `accessCode-${recipient.id}` ? styles.floatingLabelActive : styles.floatingLabelStatic}`}>Access code*</span>
                                        )}
                                        <input
                                          type="text"
                                          placeholder={focusedInputId === `accessCode-${recipient.id}` ? '' : 'Access code*'}
                                          className={`recipient-input ${errors.recipients[index] && errors.recipients[index].accessCode ? 'error' : ''}`}
                                          style={{width: '90%'}}
                                          value={recipient.accessCode}
                                          onChange={(e) => updateRecipient(recipient.id, 'accessCode', e.target.value)}
                                          onFocus={() => {
                                            setFocusedInputId(`accessCode-${recipient.id}`);
                                          }}
                                          onBlur={() => { setFocusedInputId(null);}}
                                        />
                                        {errors.recipients[index] && errors.recipients[index].accessCode && (
                                          <span className="input-error">{errors.recipients[index].accessCode}</span>
                                        )}
                                      </div>
                                      {/* Note text */}
                                      <div className={styles.smsNote}>
                                        Note: You must communicate this access code to the recipient directly.
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            {/* Toggle 2 */}
                            <div className={styles.availableSettings}>
                              <label className={`toggle-switch ${!recipient.allowRoleEdit ? styles.disabledToggle : ''}`} aria-label="Settings Toggle">
                                <input
                                  type="checkbox"
                                  checked={recipient.privateMessageSetting}
                                  disabled={!recipient.allowRoleEdit}
                                  onChange={() => toggleSetting(recipient.id, "privateMessageSetting")}
                                />
                                <span className={`${styles.toggleSlider} toggle-slider ${recipient.allowRoleEdit ? '' : styles.toggleSwitch}`}></span>
                              </label>
                              <span className= {styles.settingsOption}>Private Message</span>
                            </div>
                            {/* CHILD OPTIONS */}
                              {recipient.privateMessageSetting && (
                                <div className={styles.settingsChildren}>
                                  <textarea name="privateMessage" id="" className= {`${styles.privateMessage}`} placeholder='Enter any message that you would like to send to this recipient only.' onChange={(e) => updateRecipient(recipient.id,'privateMessage',e.target.value)}></textarea>
                                </div>
                              )}
                            {/* Toggle 3 */}
                            <div className={`${styles.availableSettings} ${recipient.role === 'reviewer' ? styles.toggleSwitchDisabled : ''}`}>
                              <label className={`toggle-switch ${!recipient.allowRoleEdit ? styles.disabledToggle : ''}`} aria-label="Settings Toggle">
                                <input
                                  type="checkbox"
                                  checked={recipient.role === 'reviewer' ? false : recipient.allowFieldConfigurationSetting}
                                  disabled={!recipient.allowRoleEdit || recipient.role === 'reviewer'}
                                  onChange={() => toggleSetting(recipient.id, "allowFieldConfigurationSetting")}
                                />
                                <span className={`${styles.toggleSlider} ${recipient.role === 'reviewer' ? styles.fieldConfigurationToggleSliderDisabled : ''} ${recipient.role === 'reviewer' ? styles.toggleSwitchDisabled : ''} toggle-slider ${recipient.allowRoleEdit ? '' : styles.toggleSwitch}`}></span>
                              </label>
                              <span className={`${styles.settingsOption}`}>Allow Field Configuration</span>
                              <button className={`${styles.fieldHelpButton} help-button`}>
                                <span className = {`${styles.iconBase} ${styles.fielConfigurationinfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="field-tooltip"></span>
                              </button>
                              <Tooltip
                                id="field-tooltip"
                                place="top"
                                className='tooltip-fieldInfo'
                                render={() => (
                                  <div className="tooltip-content">
                                    Enable this feature to allow signers to add the required fields by dragging and dropping them into the document during the signing process.
                                  </div>
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                        {/* Delivery Options End */}
                      </div>
                      {recipients.length > 1 && (
                        <div
                          className={` ${styles.delete} delete`}
                          style={{ visibility: (recipient.allowRoleDelete && recipient.allowRoleEdit) ? 'visible' : 'hidden' }}
                        >
                          <button
                            onClick={() => removeRecipient(recipient.id)}
                            disabled={!(recipient.allowRoleDelete && recipient.allowRoleEdit)}
                            aria-hidden={!(recipient.allowRoleDelete && recipient.allowRoleEdit)}
                            tabIndex={(recipient.allowRoleDelete && recipient.allowRoleEdit) ? 0 : -1}
                          >
                            <span className={`${styles.iconBase} ${styles.deleteIcon} sfIconBase sfDeleteIcon`}></span>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {documentDetails.allowNewRoles && (
                  <>
                    <button
                      className={styles.addRecipientButton}
                      onClick={addRecipient}
                      data-tooltip-id={recipients.length >= MAX_RECIPIENTS ? "max-role-tooltip" : undefined}
                    >
                      <span className = {`${styles.iconBase} ${styles.addIcon} sfIconBase sfAddIcon`}></span> Add recipient
                    </button>
                    <Tooltip
                      id="max-role-tooltip"
                      place="top"
                      className="tooltip-info"
                      render={() => (
                        <div className="tooltip-content">
                          Maximum recipient count should be less than or equal to {MAX_RECIPIENTS}
                        </div>
                      )}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="contentSection">
              <AddCC
                recipients={recipients}
                errors={errors}
                setErrors={setErrors}
                showToaster={showToaster}
                contactSuggestions={contactSuggestions}
                enableSuggestions={true}
                initialCcEmails={templateCcEmails}
                lockedEmails={templateCcEmails}
                onCcChange={setCcEmailTags}
              />
            </div>

            <div className="contentSection">
              <div className="contentBox">
                <div className="contentBoxHeader">Document Details</div>
                <div className="document-fields">
                  {(() => {
                    const tabs = getUniqueRecipientLocales();
                    const only = tabs.length === 1 ? tabs[0] : null;
                    const displayedLocale = only || activeLocale;
                    const active = displayedLocale ? (documentInfo[displayedLocale] || { title: '', message: '' }) : documents;
                    return (
                      <>
                        {tabs.length > 1 && <div role="tablist" aria-label="Document title language" className={styles.tabList}>
                          {tabs.map((loc) => {
                            const isActive = loc === activeLocale;
                            return (
                              <button
                                key={loc}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                onClick={() => setActiveLocale(loc)}
                                className={isActive ? `${styles.tabActive} ${styles.tab}` : styles.tab}
                              >
                                {languageNames[loc]}&nbsp;({loc})
                              </button>
                            );
                          })}
                        </div>}
                        <div className="field-group">
                          <label htmlFor="document-title">
                            <span>Title</span>
                            <span className = {`${styles.titleInfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="title-tooltip"></span>
                          </label>
                          <Tooltip
                            id="title-tooltip"
                            place="right"
                            className='tooltip-info tip-title'
                            render={() => (
                              <div className="tooltip-content">
                                This is the document name that will be shown within the BoldSign user interface and also in the signature request email.
                              </div>
                            )}
                          />
                          <div>
                            <input
                              id="document-title"
                              type="text"
                              placeholder="Enter document title*"
                              maxLength={256}
                              className={`document-input ${errors.documentTitle ? 'error' : ''}`}
                              value={active.title || ''}
                              onChange={(e) => {
                                updateDocumentInfoField(displayedLocale, 'title', e.target.value, active);
                              }}
                            />
                            {errors.documentTitle && <div className="error-message">{errors.documentTitle}</div>}
                          </div>
                        </div>
                        <div className="field-group">
                          <label htmlFor="document-message">Message</label>
                          <textarea
                            id="document-message"
                            placeholder={documentDetails.isAllowEditMessage ? "Enter message for all recipients" : ''}
                            className={documentDetails.isAllowEditMessage ? "document-textarea" : "document-textarea disabled"}
                            disabled={!documentDetails.isAllowEditMessage}
                            value={active.message || ''}
                            onChange={(e) => {
                              updateDocumentInfoField(displayedLocale, 'message', e.target.value, active);
                            }}
                          />
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Document Settings - Brand, tags, reassign, print & sign,
                download option, expiry and reminders. Reuses the shared
                DocumentSettingsSection component (same as DocumentSend /
                TemplateCreate) so behaviour and styling stay consistent. */}
            <DocumentSettingsSection
              brandsList={brandsList}
              onSettingsChange={handleSettingsChange}
              initialSettings={documentSettings}
              mode='templateSend'
            />
          </div>
        </>
      )}

      {/* Step 3: Send Complete */}
      {currentStep === 2 && (
        <div className={styles.templateSendCompleteContainer}>
          <iframe
            id="template-iframe"
            ref={iframeRef}
            src={iframeSrc}
            title="Complete Template Send"
            style={{ width: '100%', height: '100vh', border: 'none' }}
          />
        </div>
      )}
    </div>
  );
};

export default TemplateSend;
